import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, Save, Eye, Edit, Trash2, AlertTriangle, RefreshCw, MoreVertical, ShoppingCart, CheckCircle, Mail, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSisConfig } from '../../contexts/SisConfigUi';
import { logComponentError } from '../../utils/errFrontend';
import SqlexecMensaje from './SqlexecMensaje';
import { useUniqueValidation } from '../../hooks/useUniqueValidation';
import { useFormNavigation } from '../../hooks/useFormNavigation';
import { calcularTotalesPartida, calcularTotalesGenerales, evaluateValidationRule, validateGreaterThan } from '../../utils/calculations';
import SearchModalField from '../common/SearchModalField';
import useFieldSanitization from '../../hooks/useFieldSanitization';
import { formatNumber, formatTotalNumber, sanitizeNumericInput } from '../../utils/numberFormatters';
import { sendEmail, resolvePlaceholders } from '../../utils/envioEmails';
import AttachmentsField from '../common/AttachmentsField';
import { secureFetch } from '../../utils/authInterceptor';
import SqlexecConfirmDialog from './SqlexecConfirmDialog';
import SqlexecIdTag from '../common/SqlexecIdTag';
import { evaluarCondicion, conservarCursor, compensarEscalaVh } from '../../utils/formHelpers';

const CONFIG = { 
  maxRetries: 3, 
  retryDelayMultiplier: 1000 
};

const evalRule = (op, left, right) => ({
  '==': String(left) === String(right),
  '!=': String(left) !== String(right),
  '>':  Number(left) > Number(right),
  '<':  Number(left) < Number(right),
}[op] ?? false);

const applyConditionalDefaults = (schemaObj, dataDraft, touchedMap, isEditMode = false) => {
  if (!schemaObj?.fields) return dataDraft;
  const updates = {};

  for (const f of schemaObj.fields) {
    const cond = f.conditionalDefault;
    if (!cond) continue;

    const depVal = dataDraft[cond.dependsOn];
    const ruleMatch = cond.rules?.find(r => evalRule(r.operator, depVal, r.value));

    if (touchedMap?.[f.name]) continue;

    const currentVal = dataDraft[f.name];

    if (ruleMatch) {
      if (currentVal === '' || currentVal === null || currentVal === undefined) {
        updates[f.name] = ruleMatch.set;
      }
    } else {
      // 🔥 En UPDATE/VIEW respetar valor existente de initialData
      if (f.defaultValue !== undefined && currentVal !== f.defaultValue) {
          if (isEditMode) continue;
          updates[f.name] = f.defaultValue;
      }
    }
  }

  return Object.keys(updates).length ? { ...dataDraft, ...updates } : dataDraft;
};

const resolveSimpleDefault = (field) => {
  
  const { defaultValue, type } = field;
  
  if (defaultValue === undefined || defaultValue === null) return null;
  
  if (type === 'date' && (defaultValue === 'TODAY' || defaultValue === 'today')) {
    return new Date().toISOString().split('T')[0];
  }
  
  if (type === 'datetime-local' && (defaultValue === 'NOW' || defaultValue === 'now')) {
    return new Date().toISOString().slice(0, 16);
  }
  
  return defaultValue;
};

const createEmptyDetailRow = (detailConfig, rowNumber) => {
  const emptyRow = {
    _id: `new_${Date.now()}_${rowNumber}`,
    numero: rowNumber
  };
  
  detailConfig.columns?.forEach(col => {
    if (col.autoIncrement) return;
    
    if (col.defaultValue !== undefined) {
      emptyRow[col.name] = col.defaultValue;
    } else if (col.type === 'number') {
      emptyRow[col.name] = 0;
    } else {
      emptyRow[col.name] = '';
    }
  });
  
  detailConfig.hiddenFields?.forEach(field => {
    if (field.defaultValue !== undefined) {
      emptyRow[field.name] = field.defaultValue;
    } else {
      emptyRow[field.name] = null;
    }
  });
  emptyRow.partida_completa = false;  
  return emptyRow;
};

// ==========================================
// COMPONENTE: DropdownMenu para Detail Grid
// ==========================================

/**
 * Mapea nombres de iconos a componentes de Lucide React
 */
const getIcon = (iconName) => {
  const icons = {
    edit: Edit,
    trash: Trash2
  };
  return icons[iconName] || Edit;
};

// Factor de la escala automatica de la interfaz (index.js lo publica en
// --ui-escala sobre <html>); 1 sin escala. Mismo criterio que SqlexecTable.jsx:
// getBoundingClientRect y window.innerHeight vienen en px reales de pantalla,
// pero el top/left del menu se multiplica por el zoom, asi que se dividen.
const leerEscalaInterfaz = () => {
  const escala = parseFloat(document.documentElement.style.getPropertyValue('--ui-escala'));
  return escala > 0 ? escala : 1;
};

/**
 * Menú dropdown con posicionamiento inteligente
 * Se posiciona automáticamente para no salirse de pantalla
 */
const DropdownMenu = ({ item, accionesDisponibles, onAccionClick, disabled = false, tabla_ui }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  
  const handleToggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // 🔥 Si está disabled, no abrir menú
    if (disabled) return;

    if (!isOpen) {
      const escala = leerEscalaInterfaz();
      const rectReal = e.target.getBoundingClientRect();
      const rect = {
        top:    rectReal.top / escala,
        bottom: rectReal.bottom / escala,
        left:   rectReal.left / escala,
        right:  rectReal.right / escala
      };
      const menuHeight = accionesDisponibles.length * 40;
      const menuWidth = 140;
    
      let top = rect.bottom + 8;
      let left = rect.right - menuWidth;
    
      // Ajustar si se sale por abajo
      if (top + menuHeight > window.innerHeight / escala) {
        top = rect.top - menuHeight - 8;
      }
    
      // Ajustar si se sale por la izquierda
      if (left < 0) {
        left = rect.left;
      }
    
      setMenuPosition({ top, left });
    }
    setIsOpen(!isOpen);
  };

  const getColorClass = (color) => {
    const colorMap = {
      red: 'text-red-600 hover:bg-red-50',
      blue: 'text-blue-600 hover:bg-blue-50',
      green: 'text-green-600 hover:bg-green-50',
      gray: 'text-gray-700 hover:bg-gray-50',
    };
    return colorMap[color] || colorMap.gray;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className="rounded transition-colors"
          style={{ 
              padding: tabla_ui?.actionsColumn?.menuButton?.padding || '0.375rem 0.75rem',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: disabled ? '#d1d5db' : '#6b7280',
              borderRadius: tabla_ui?.actionsColumn?.menuButton?.borderRadius || '50%',
              transitionDuration: '150ms'
          }}
          onMouseEnter={(e) => {
              if (!disabled) {
                  e.currentTarget.style.backgroundColor = tabla_ui?.actionsColumn?.menuButton?.hoverBackgroundColor || '#ccfbf1';
                  e.currentTarget.style.color = tabla_ui?.actionsColumn?.menuButton?.hoverTextColor || '#14b8a6';
              }
          }}
          onMouseLeave={(e) => {
              if (!disabled) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6b7280';
              }
          }}
          title={disabled ? 'Completa la partida para ver acciones' : 'Acciones'}
      >
          <MoreVertical size={parseInt(tabla_ui?.actionsColumn?.menuButton?.iconSize) || 16} />
      </button>
      
      {isOpen && !disabled && (
        <>
          {/* Overlay para cerrar al hacer click afuera */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          ></div>
          
          {/* Menu flotante */}
          <div 
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]"
            style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left}px` }}
          >
            {accionesDisponibles.map((accion) => {
              const IconComponent = getIcon(accion.icon);
              const colorClass = getColorClass(accion.color);
              
              return (
                <button
                  key={accion.key}
                  type="button"
                  onClick={() => {
                    onAccionClick(accion, item);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-2 text-xs text-left flex items-center ${colorClass} transition-colors`}
                  title={accion.label}
                >
                  <IconComponent size={14} className="mr-2" />
                  {accion.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const SqlexecMasterDetail = ({
  componentForm,
  mode = 'create',
  initialData = {},
  parentRow,
  userPermissionLevel = 'edit',
  onClose,
  onSuccess,
  useGenerico = false,
  idreporte = null,
  servicioClave = null,
  asyncEmail = false,
  surtirMode = false,
}) => {

  //console.log('🔍 [STEP 2] SqlexecMasterDetail - surtirMode recibido:', surtirMode);

  const { tabla_ui, formulario_ui, botones_ui, modales_ui, panel_ui } = useSisConfig();
  const headerConfig = tabla_ui?.header || {};
  const rowsConfig = tabla_ui?.rows || {};
  const columnsConfig = tabla_ui?.columns || {};
  const [schema, setSchema] = useState(null);
  const [idsqlexec, setIdsqlexec] = useState(null); // identificador SQLEXEC real (fila de config)
  const [moduleUIConfig, setModuleUIConfig] = useState({})
  const [formData, setFormData] = useState({});

  // ==========================================
  // ENCABEZADO COLAPSABLE (schema.config.collapsibleHeader)
  // Bloque autocontenido -- extraible a un hook compartido con
  // SqlexecMasterDetailLight. Config: { enabled, label, enable_when,
  // fillDetailHeight }. enable_when usa el vocabulario de evaluarCondicion
  // (mismo de visible_when/disabled_when); sin enable_when siempre se puede
  // colapsar. Sin la clave collapsibleHeader no cambia nada del render.
  // ==========================================
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const collapsibleHeaderCfg = schema?.config?.collapsibleHeader;
  const isCollapsibleHeader  = collapsibleHeaderCfg === true || collapsibleHeaderCfg?.enabled === true;
  const canCollapseHeader    = isCollapsibleHeader && evaluarCondicion(collapsibleHeaderCfg?.enable_when, formData);
  const headerIsCollapsed    = isCollapsibleHeader && headerCollapsed;
  const fillDetailCollapsed  = headerIsCollapsed && collapsibleHeaderCfg?.fillDetailHeight === true;
  // Si la condicion deja de cumplirse estando colapsado, se reabre.
  useEffect(() => {
    if (headerCollapsed && !canCollapseHeader) setHeaderCollapsed(false);
  }, [headerCollapsed, canCollapseHeader]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [idField, setIdField] = useState(null);
  const [originalTimestamp, setOriginalTimestamp] = useState(
    initialData._original_updated_at || initialData.actualizada
  );
  const [conflictData, setConflictData] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [selectOptions, setSelectOptions] = useState({});
  const [loadingField, setLoadingField] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [detailSchema, setDetailSchema] = useState(null);
  const [detailData, setDetailData] = useState([]);
  const [focusedCell, setFocusedCell] = useState(null);

  // ===========================================
  // WATCHDOG focusedCell
  // ===========================================
  useEffect(() => {
    if (!focusedCell) return;
    const timer = setTimeout(() => {
      console.warn(
        '⚠️ focusedCell reset watchdog'
      );
      setFocusedCell(null);
    }, 10000);
    return () => clearTimeout(timer);
  }, [focusedCell]);

  const [invalidCodigoRow, setInvalidCodigoRow] = useState(null);
  const [totalsSchema, setTotalsSchema] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [searchModalState, setSearchModalState] = useState({
    isOpen: false,
    rowIndex: null,
    fieldName: null,
    config: null
  });
  const [searchModalOriginInput, setSearchModalOriginInput] = useState(null);
  const searchModalSelectedRef = useRef(false); // hubo selección en Shift+B → no regresar foco al código
  const [showConfirmaDelete, setShowConfirmaDelete] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [showConfirmaMensaje, setShowConfirmaMensaje] = useState(false);
  const [showConfirmaEmail, setShowConfirmaEmail] = useState(false);
  // Saldo de EMAILS -- consulta propia al abrir Sys_Envio_Email (universal,
  // no depende de que el modulo padre agregue una columna a su sqlexec_list).
  // null = aun no se resolvio ; -1 = servicio no configurado (sin fila en
  // mapasoft.servicios) ; 0 = sin saldo ; >0 = saldo disponible.
  const [saldoCorreos, setSaldoCorreos] = useState(null);
  const [priceTooltipRow, setPriceTooltipRow] = useState(null);
  const [priceTooltipPosition, setPriceTooltipPosition] = useState({ top: 0, left: 0 });
  const priceTooltipTimer = useRef(null);
  const [totals, setTotals] = useState({
    importe: 0,
    descuento: 0,
    subtotal: 0,
    iva: 0,
    subtotal_con_iva: 0,
    ieps: 0,
    retencion_iva: 0,
    retencion_isr: 0,
    total: 0
  });

  const [preservedValues, setPreservedValues] = useState({});
  const [emailConfig, setEmailConfig] = useState(null);
  const [confirmHeader, setConfirmHeader] = useState(null);
  const [confirmBtnLabel, setConfirmBtnLabel] = useState(null);
  const isDelete        = mode === 'delete';
  const isUpdate        = mode === 'update';
  const isCreate        = mode === 'create';
  const isComponentMode = !!initialData?._title_override && isUpdate;
  const isReadOnly      = mode === 'view' || isComponentMode;
  const loadedRef = useRef(false);
  const totalsRef = useRef(null);
  const formRef = useRef(null);
  // Se activa en handleEditDetailRow (re-editar una partida EXISTENTE) y se
  // consume/limpia la primera vez que esa fila vuelve a completeRow --
  // evita el salto de foco/scroll a "+ Agregar Partida" que solo tiene
  // sentido al capturar una fila NUEVA, no al corregir una ya existente.
  const isReeditingDetailRowRef = useRef(false);
  const scrollToTotals = useCallback(() => {
    setTimeout(() => {
      totalsRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end' 
      });
    }, 150);
  }, []);
  const [showMensaje, setShowMensaje] = useState(false);
  const [mensajeData, setMensajeData] = useState({
    type: 'error',
    title: '',
    message: '',
    steps: [],
    technicalInfo: null
  });

  const uniqueValidation = useUniqueValidation(
    componentForm,
    mode,
    initialData?.[idField]
  );

  const formNavigation = useFormNavigation(
    schema?.fields || [],
    formData,
    isReadOnly || mode === 'view' || mode === 'delete'
  );

  const { sanitizeOnType, sanitizeOnBlur } = useFieldSanitization();

  useEffect(() => {
    Object.entries(uniqueValidation.errors).forEach(([fieldName, error]) => {
      if (error?.shouldClear) {
        setFormData(prev => ({ ...prev, [fieldName]: '' }));
        setTimeout(() => {
          uniqueValidation.clearError(fieldName);
        }, 2000);
      }
    });
  }, [uniqueValidation.errors, uniqueValidation.clearError]);

  const safeGet = (obj, key, fallback = '') => (obj?.[key] ?? fallback);
  const showMensajeModal = (type, title, message, steps = [], technicalInfo = null, onDismiss = null) => {
    setMensajeData({ type, title, message, steps, technicalInfo, onDismiss });
    setShowMensaje(true);
  };

  const loadSelectOptions = async (field) => {
    if (!field?.dataSource?.query) return [];
    const hasParams = field.dataSource.params && field.dataSource.params.length > 0;
    if (hasParams) {
      const missingParams = field.dataSource.params.some(
        paramName => !formData[paramName] || formData[paramName] === ''
      );      
      if (missingParams) {
        setSelectOptions(prev => ({ ...prev, [field.name]: [] }));
        return [];
      }
    }    
    setLoadingField(p => ({ ...p, [field.name]: true }));
    for (let attempt = 0; attempt < CONFIG.maxRetries; attempt++) {
      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const endpoint = hasParams 
          ? '/api/sqlexec_generico/query-params'
          : '/api/sqlexec/query';
        const payload = { query: field.dataSource.query };
        if (hasParams) {
          payload.params = field.dataSource.params.map(
            paramName => formData[paramName]
          );
        }
        const r = await secureFetch(endpoint, {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        });
        if (r.status === 429 && attempt < CONFIG.maxRetries - 1) {
          await new Promise(res => setTimeout(res, CONFIG.retryDelayMultiplier * (attempt + 1)));
          continue;
        }

        const j = await r.json();
        if (j?.success) {
          const options = j.data || [];
          setSelectOptions(prev => ({ ...prev, [field.name]: options }));
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return options;
        } else {
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return [];
        }
      } catch (e) {
        if (attempt === CONFIG.maxRetries - 1) {
          console.error(`Error cargando opciones de ${field.name}:`, e);
          toast.warning(`No se pudieron cargar las opciones de ${field.label}`);
          setLoadingField(p => ({ ...p, [field.name]: false }));
          return [];
        }
      }
    }

    setLoadingField(p => ({ ...p, [field.name]: false }));
    return [];
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    //console.log('🔍 initialData.c_regimenfiscal:', initialData?.c_regimenfiscal);

    (async () => {
      if (isDelete) {
        const minimalSchema = {
          title: componentForm,
          fields: []
        };
        setSchema(minimalSchema);
        const possibleIdFields = Object.keys(initialData || {}).filter(
          key => key.toLowerCase().includes('id') && initialData[key]
        );        
        if (possibleIdFields.length > 0) {
          setIdField(possibleIdFields[0]);
        } else {
          toast.error('No se pudo identificar el campo ID del registro');
          setLoading(false);
          return;
        }        
        setFormData({});
        setLoading(false);
        return;
      }

      try {
        const token = sessionStorage.getItem('mapasac_access_token');

        let r, j;
        if (useGenerico) {
          r = await secureFetch(`/api/sqlexec_generico/schema/${componentForm}?idreporte=${idreporte || ''}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          j = await r.json();
          //console.log('🎯 [useGenerico] respuesta schema:', j);
        } else {
          r = await secureFetch(`/api/sqlexec/${componentForm}/read?_t=${Date.now()}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          });
          j = await r.json();
        }

        if (!j?.success) {
          toast.error('Error cargando esquema del formulario (S)');
          setLoading(false);
          return;
        }

        const s = j?.data?.schema;

        if (!s) {
          logComponentError('SqlexecMasterDetail', 'load_schema', 
            `Schema not found: ${componentForm}`);
          
          setLoading(false);
          showMensajeModal(
            'error',
            'Schema no configurado',
            'El formulario no puede mostrarse porque no tiene schema configurado en la base de datos.',
            [
              'Accede a PostgreSQL → base de datos "empresas"',
              `Ejecuta: SELECT * FROM sisconfig.sqlexec WHERE component_form = '${componentForm}'`,
              'Verifica que el campo "schema_form" contenga JSON válido',
              'Estructura requerida: { "title": "string", "fields": [], "primaryKey": "string" }',
              'Si está vacío ({}), copia un schema_form de otro módulo similar como plantilla'
            ],
            {
              component_form: componentForm,
              database: 'empresas',
              table: 'sisconfig.sqlexec',
              field: 'schema_form'
            }
          );
          return;          
        }

        // 🔥 AGREGAR sqlexec_calculos al schema
        if (j?.data?.sqlexec_calculos) {
          s.sqlexec_calculos = j.data.sqlexec_calculos;
        }

        setSchema(s);
        setIdsqlexec(j?.data?.idsqlexec ?? null);
        const uiConfig = s?.ui || {};
        setModuleUIConfig(uiConfig);

        if (j?.data?.emailConfig) {
          setEmailConfig(j.data.emailConfig);
        }

        if (s.detail) {
          setDetailSchema(s.detail);
          if (isCreate) {
            const minRows = s.detail.minRows || 0;
            if (minRows > 0) {
              const emptyRows = [];
              for (let i = 0; i < minRows; i++) {
                emptyRows.push(createEmptyDetailRow(s.detail, i + 1));
              }
              setDetailData(emptyRows);
            } else {
              setDetailData([]);
            }
          }

          // 🔥 NUEVO: Cargar detalles existentes para view/update
          if (isUpdate || isReadOnly) {
            try {
              const masterId = initialData[s.primaryKey];
              const detailTable = s.detail.table;
              const detailFK    = s.detail.foreignKey;
              const detailPK    = s.detail.primaryKey;

              const query = (surtirMode && s.detail.loadQuerySurtir)
              ? s.detail.loadQuerySurtir
              : (s.detail.loadQuery ||
                `SELECT * FROM mapasoft.${detailTable} WHERE ${detailFK} = $1 ORDER BY ${detailPK}`);

              const resDetail = await secureFetch('/api/sqlexec_generico/query-params', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ query, params: [masterId] })
              });

              const jsonDetail = await resDetail.json();
              if (jsonDetail?.success && jsonDetail?.data?.length > 0) {
                const renglones = jsonDetail.data.map((row, idx) => {
                  const totalesPartida = calcularTotalesPartida(
                    row,
                    formData.cotizacion_esbase ?? true,
                    formData.tipocamb_valor ?? 1,
                    null, 
                    initialData.c_regimenfiscal ?? '000'
                  );

                  //console.log('🔍 precio_actual_catalogo:', row.precio_actual_catalogo, 'precio:', row.precio);

                  return {
                    ...row, //original
                    _id: `existing_${row[detailPK]}`, //original
                    numero: idx + 1, //original
                    partida_completa: true, //original
                    codigo: row.idinvearts, //original
                    total: isComponentMode ? 0 : row.imptotal, //original
                    surtir_total: false, //agregado por pedido-remision
                    
                    imptotal: isComponentMode ? 0 : row.imptotal, //agregado por pedido-remision
                    impiva: isComponentMode ? 0 : row.impiva, //agregado por pedido-remision
                    impdesc: isComponentMode ? 0 : row.impdesc, //agregado por pedido-remision
                    
                    _display_unidad: row.unidad, //original
                    _display_porcentaje_iva: row.porcentaje_iva, //original
                    _precio_alerta: parseFloat(row.precio_actual_catalogo) > parseFloat(row.precioreal), //original
                    prelibre: row.prelibre, //original
                    impuestos_extra: isComponentMode ? [] : totalesPartida.impuestos_extra, //(row.tasas_extra || []), //original
                    importe_bruto: isComponentMode ? 0 : parseFloat((parseFloat(row.imptotal) + parseFloat(row.impdesc)).toFixed(2)), //row.imptotal original
                  };
                });
                setDetailData(renglones);
              } else {
                  setDetailData([]);
              }
            } catch (e) {
              console.error('❌ Error cargando detalles:', e);
              setDetailData([]);
            }
          }
        }

        if (s.totals) {
          setTotalsSchema(s.totals);
        }

        if (s?.fields) {
          const primaryField = s.fields.find(f => f.primary === true);
          if (primaryField) {
            setIdField(primaryField.name);
          } else {
            console.error('❌ ERROR: No se encontró campo primary en schema');  
            logComponentError('SqlexecMasterDetail', 'load_schema', 
              `No primary field found: ${componentForm}`);
            
            setLoading(false);
            showMensajeModal(
              'error',
              'Campo ID no definido',
              'El schema existe pero ningún campo está marcado como primaryKey (campo ID).',
              [
                `Edita el registro en sisconfig.sqlexec para component_form = '${componentForm}'`,
                'Dentro de schema_form → fields, encuentra el campo ID (ej: idclientes, idinvearts)',
                'Agrega la propiedad: "primary": true',
                'Ejemplo: { "name": "idclientes", "type": "number", "primary": true }',
                'Guarda los cambios y recarga el formulario'
              ],
              {
                component_form: componentForm,
                schema_fields: s.fields?.length || 0,
                error: 'No field with primary: true found'
              }
            );
            return;
            
          }
        } else {
          console.error('❌ ERROR: Schema no configurado');
  
          logComponentError('SqlexecMasterDetail', 'load_schema', 
            `Schema has no fields: ${componentForm}`);
          
          setLoading(false);
          showMensajeModal(
            'error',
            'Schema sin campos',
            'El schema existe pero no tiene campos definidos (fields está vacío).',
            [
              `Edita el schema_form en sisconfig.sqlexec para '${componentForm}'`,
              'Agrega array "fields" con al menos un campo',
              'Ejemplo: fields: [{ name: "nombre", type: "text", label: "Nombre" }]',
              'Incluye un campo con primary: true',
              'Guarda y recarga el formulario'
            ],
            {
              component_form: componentForm,
              error: 'schema.fields is undefined or empty'
            }
          );
          return;
        }

        let loadedParams = {};
        //presenta todos los campos invokados
        //console.log('🔍 FIELDS NULL CHECK:', s.fields?.map((f, i) => `[${i}] ${f?.name || 'NULL'}`));
        const needsGlobalParams = s.fields?.some(f => 
          f.defaultValue && 
          typeof f.defaultValue === 'object' && 
          (f.defaultValue.type === 'date_offset' || f.defaultValue.type === 'numeric_param') && 
          f.defaultValue.offset?.source === 'global_param'
        );

        if (needsGlobalParams) {
          try {
            const paramsResponse = await secureFetch('/api/sqlexec/query', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                query: 'SELECT * FROM mapasoft.cliepara WHERE idsucursal = :session_idsucursal LIMIT 1'
              })
            });

            const paramsJson = await paramsResponse.json();

            if (paramsJson?.success && paramsJson?.data?.length > 0) {
              loadedParams = paramsJson.data[0];
            } else {
              let sucursalLabel = '';
              try {
                const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
                sucursalLabel = session?.empresa?.nombre || `#${session?.user?.idsucursal ?? ''}`;
              } catch { /* noop */ }

              setLoading(false);
              showMensajeModal(
                'error',
                'Sucursal sin parámetros configurados',
                `La sucursal ${sucursalLabel} no tiene parámetros configurados en cliepara. Contacte al administrador para configurar.`,
                [
                  'Verifica que exista una fila en mapasoft.cliepara para el idsucursal de esta sucursal',
                  'Si no existe, un administrador debe crearla con dias_vigencia_cotizacion, dias_vigencia_pedido y descuento_maximo_permitido'
                ],
                { component_form: componentForm, tabla: 'mapasoft.cliepara' }
              );
              return;
            }
          } catch (error) {
            console.error('Error cargando parámetros globales:', error);
          }
        }

        const resolveDefaultValueWithParams = (field, params) => {
          
          const { defaultValue, type } = field;          
          if (defaultValue === undefined || defaultValue === null) return null;

          if (typeof defaultValue === 'string') {
            if (type === 'date' && (defaultValue === 'TODAY' || defaultValue === 'today')) {
              return new Date().toISOString().split('T')[0];
            }
            if (type === 'datetime-local' && (defaultValue === 'NOW' || defaultValue === 'now')) {
              return new Date().toISOString().slice(0, 16);
            }
            return defaultValue;
          }
          
          // ✅ AQUÍ — fuera del string, al mismo nivel que date_offset
          if (typeof defaultValue === 'object' && defaultValue.type === 'numeric_param') {
            const { offset } = defaultValue;
            if (offset?.source === 'global_param') {
              return parseFloat(params[offset.field]) || 0;
            }
            return defaultValue.base || 0;
          }
          
          if (typeof defaultValue === 'object' && defaultValue.type === 'date_offset') {
            const { base, offset } = defaultValue;
            let baseDate = new Date();
            let offsetDays = 0;
            if (offset.source === 'static') {
              offsetDays = offset.value || 0;
            } else if (offset.source === 'global_param') {
              offsetDays = params[offset.field] || 0;
            }
            baseDate.setDate(baseDate.getDate() + Number(offsetDays));            
            return baseDate.toISOString().split('T')[0];
          }          

          return defaultValue;
        };

        // Collect all field names declared as returnFields in search-modal fields so
        // they can be pre-populated from parentRow on create (same effect as selecting
        // the client manually via the search modal on a fresh form).
        const searchReturnFieldNames = new Set();
        s.fields?.forEach(f => {
          if (f.type === 'search-modal') {
            (f.searchConfig?.returnFields || []).forEach(rf => searchReturnFieldNames.add(rf));
          }
        });

        const base = {};
        s.fields?.forEach(f => {

          //if (!f || !f.type) {
          //  console.log('🔴 CAMPO NULL O SIN TYPE:', f);
          //  return;
          //}
          //console.log('🔍 campo:', f.name, '| tipo:', f.type, '| parentField:', f.parentField, '| parentRow valor:', parentRow?.[f.parentField || f.name]);

          if ((isUpdate || isReadOnly || isDelete) && initialData.hasOwnProperty(f.name)) {
            const rawVal = initialData[f.name];
            base[f.name] = f.type === 'select'
              ? (rawVal === null || rawVal === undefined) ? '0' : String(rawVal)
              : safeGet(initialData, f.name, '');
            // 🔥 Si es search-modal, cargar también su _display
            if (f.type === 'search-modal' && initialData[`${f.name}_display`]) {
              base[`${f.name}_display`] = initialData[`${f.name}_display`];
            }
          } else if (isCreate && initialData?.hasOwnProperty(f.name) && initialData[f.name] !== '' && initialData[f.name] !== null && initialData[f.name] !== undefined) {
            base[f.name] = initialData[f.name];
          } else if (isCreate && (f.disabled || f.type === 'hidden' || f.prefillFromParent) && parentRow?.[f.name]) {
            base[f.name] = parentRow[f.name];
          } else if (isCreate && searchReturnFieldNames.has(f.name) && parentRow?.[f.name] != null && parentRow[f.name] !== '') {
            base[f.name] = parentRow[f.name];
          } else {
            base[f.name] = resolveDefaultValueWithParams(f, loadedParams);
          }
        });

        //console.log('🔍 base.c_regimenfiscal:', base.c_regimenfiscal);

        if (j?.data?.emailConfig?.adjuntos?.default) {
          base['adjuntos'] = j.data.emailConfig.adjuntos.default.map(a => ({
            ...a,
            esDefault: true
          }));
        }

        if (s?.confirm_messages && initialData?.titulo !== undefined) {
          const modeKey = initialData?.mode || 'create';
          const cm = s.confirm_messages[modeKey] || {};
          const resolve = (txt) => txt
            ?.replace('{{titulo}}',   initialData?.titulo   || '')
            ?.replace('{{partidas}}', initialData?.partidas || '0')
            ?.replace('{{total}}',    initialData?.total    || '0.00')
            ?.replace('{{label}}',    initialData?.label    || '')
            ?.replace('{{email}}',    initialData?.label    || '')
            ?.replace('{{asunto}}',   initialData?.total    || '');

          base['titulo']     = resolve(cm.titulo);
          base['mensaje']    = resolve(cm.mensaje);
          base['submensaje'] = resolve(cm.submensaje);
          base['label_submensaje'] = cm.label_submensaje || 'Total';
          if (cm.header) {
            setConfirmHeader({
              ...cm.header,
              btn_color: cm.btn_color || null,
              btn_hover: cm.btn_hover || null
            });
          }
          if (cm.btn_confirm) {
            setConfirmBtnLabel(cm.btn_confirm);
          }
        }

        // ← AGREGAR — copiar campos extra de initialData para useGenerico
        if (isCreate && useGenerico && initialData) {
          Object.keys(initialData).forEach(key => {
            if (!base.hasOwnProperty(key)) {
              base[key] = initialData[key];
            }
          });
        }

        const initialized = applyConditionalDefaults(s, base, {}, isUpdate || isReadOnly);
        
        setFormData(initialized);

        if (isUpdate && initialData) {
          const initialPreserved = {};
          
          s.fields?.forEach(f => {
            if (initialData.hasOwnProperty(f.name) && initialData[f.name] != null) {
              initialPreserved[f.name] = initialData[f.name];
            }
          });
          
          setPreservedValues(initialPreserved);
        }

        // Saldo de EMAILS -- consulta propia, universal (funciona para
        // cualquier modulo que abra Sys_Envio_Email en el futuro sin
        // depender de que agregue una columna saldo_correos a su propia
        // lista). Mismo patron que saldo_timbres en Generar Factura, pero
        // resuelto aqui en vez de heredado de parentRow.
        //
        // Se resuelve ANTES de setLoading(false): mientras loading sigue true
        // solo se ve el spinner, no el form. Asi el modal de bloqueo y el
        // form quedan en el mismo paint (sin el parpadeo "form visible ->
        // luego modal"). En timbrado no hace falta porque saldo_timbres ya
        // viene sincrono en parentRow desde la grilla.
        if (componentForm === 'Sys_Envio_Email' && isCreate) {
          try {
            const rSaldo = await secureFetch('/api/sqlexec/query', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: "SELECT CASE WHEN NOT EXISTS (SELECT 1 FROM mapasoft.servicios s WHERE s.clave = 'EMAILS' AND s.tenant_id = :session_tenant_id) THEN -1 ELSE (SELECT COALESCE(SUM(s.saldo - s.utilizado), 0) FROM mapasoft.servicios s WHERE s.clave = 'EMAILS' AND s.activo = true AND s.tenant_id = :session_tenant_id) END AS saldo_correos"
              })
            });
            const jSaldo = await rSaldo.json();
            const raw = jSaldo?.data?.[0]?.saldo_correos;
            const valorSaldo = (raw === undefined || raw === null) ? null : parseInt(raw);
            setSaldoCorreos(valorSaldo);
            // Mismos 2 mensajes que el preflight de guardado (backend/utils/servicios.js):
            // -1 = servicio no configurado ; 0 = sin saldo.
            if (valorSaldo === -1) {
              // Mismo formato estandar que cliepara/invepara: titulo + cuerpo +
              // pasos numerados + info tecnica.
              showMensajeModal('error', 'Servicio de correos no configurado',
                'El servicio de envío de correos no está configurado para tu empresa. Contacte al administrador para configurar.',
                [
                  "Verifica que exista una fila en mapasoft.servicios para el tenant_id de esta empresa con clave='EMAILS'",
                  'Si no existe, un administrador debe crearla con saldo y utilizado'
                ],
                { component_form: componentForm, tabla: 'mapasoft.servicios' });
            } else if (valorSaldo === 0) {
              showMensajeModal('error', 'Sin saldo de correos disponibles',
                'No es posible enviar el correo: no hay saldo de envíos disponible para tu empresa. Contacta al administrador para recargar.');
            }
          } catch (e) {
            console.error('Error consultando saldo de correos:', e);
          }
        }

        setLoading(false);

        if (!isDelete) {
          const independentSelects = s.fields?.filter(
            f => f.type === 'select' && !f.dataSource?.params
          ) || [];
          
          await Promise.allSettled(independentSelects.map(loadSelectOptions));
          
          if ((isUpdate || isReadOnly) && initialData) {
            const dependentSelects = s.fields?.filter(
              f => f.type === 'select' && f.dataSource?.params
            ) || [];
            
            for (const field of dependentSelects) {
              const hasAllParams = field.dataSource.params.every(
                paramName => initialized[paramName] && initialized[paramName] !== ''
              );
              
              if (hasAllParams) {
                try {
                  const payload = {
                    query: field.dataSource.query,
                    params: field.dataSource.params.map(paramName => initialized[paramName])
                  };
                  
                  const optionsRes = await secureFetch('/api/sqlexec_generico/query-params', {
                    method: 'POST',
                    headers: { 
                      Authorization: `Bearer ${token}`, 
                      'Content-Type': 'application/json' 
                    },
                    body: JSON.stringify(payload)
                  });
                  
                  const optionsData = await optionsRes.json();
                  if (optionsData?.success) {
                    setSelectOptions(prev => ({ 
                      ...prev, 
                      [field.name]: optionsData.data || [] 
                    }));
                  }
                } catch (e) {
                  console.error(`Error pre-cargando ${field.name}:`, e);
                }
              }
            }
          }
        }
      } catch (e) {
        console.error('Error cargando schema:', e);
        logComponentError('SqlexecMasterDetail', 'load_schema', 
        `${componentForm} - ${e.message}`);
        toast.error('Error de conexión');
        setLoading(false);
      }
    })();
  }, [componentForm, initialData, isDelete, isReadOnly, isUpdate]);

  useEffect(() => {
    if (loading) return;
    if (!isCreate && !isUpdate) return;
    setTimeout(() => {
      const firstInput = formRef.current?.querySelector(
    'textarea:not([disabled]):not([readonly]), input:not([disabled]):not([readonly]):not([type="hidden"])'
  );
      if (firstInput) firstInput.focus();
    }, 500);
  }, [loading]);

  useEffect(() => {
    if (!schema?.fields) return;
    if (isReadOnly) return;
    if (isUpdate && !Object.keys(touched).length) return;
    
    let updates = {};
    
    schema.fields?.forEach(field => {
      if (field.conditionalDefault) {
        const { dependsOn, rules } = field.conditionalDefault;        
        if (isUpdate && !touched[dependsOn]) return;        
        if (isCreate && !touched[dependsOn]) return;        
        const dependentValue = formData[dependsOn];        
        const rule = rules.find(r => {          
          if (r.operator === '==') return String(r.value) === String(dependentValue);
          if (r.operator === '!=') return String(r.value) !== String(dependentValue);
          return false;
        });
        
        if (rule && !touched[field.name]) {
          let newValue;          
          if (rule.set === "preserved") {
            if (preservedValues[field.name] !== undefined && preservedValues[field.name] !== null) {
              newValue = preservedValues[field.name];
            } else {
              newValue = field.defaultValue ?? '';
            }
          } else {
            newValue = rule.set;
          }          
          if (formData[field.name] !== newValue) {
            updates[field.name] = newValue;
          }
        }        
      }
    });
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
    }
  }, [formData, schema, touched, isReadOnly, isUpdate, isCreate, preservedValues]);

  const validateCompleteForm = () => {
    if (!schema?.fields) return false;
    const errs = {};
    let ok = true;

    schema.fields?.forEach(f => {
      if (f.conditionalVisible) {
        const { dependsOn, rules } = f.conditionalVisible;
        const depVal = formData[dependsOn];
        const rule = rules?.find(r => String(r.value) === String(depVal));
        if (rule && rule.visible === false) return;
      }

      let required = !!f.required;
      if (f.conditionalRequired) {
        const { dependsOn, rules } = f.conditionalRequired;
        const depVal = formData[dependsOn];
        const rule = rules?.find(r => String(r.value) === String(depVal));
        if (rule) required = !!rule.required;
      }

      const v = formData[f.name];
      const empty = v === '' || v === null || v === undefined;
      ///if (required && empty) {
      ///  console.log('🔍 campo requerido vacío:', f.name, '| valor:', v);
      ///}
      if (required && empty) {
        errs[f.name] = `${f.label} es requerido`;
        ok = false;
      }
    });

    Object.entries(uniqueValidation.errors).forEach(([fieldName, error]) => {
      errs[fieldName] = error?.message || error;
      ok = false;
    });

    Object.entries(errors).forEach(([fieldName, error]) => {
      const field = schema?.fields?.find(f => f.name === fieldName);
      
      if (field?.type === 'number' && field?.validation && error) {
        errs[fieldName] = error;
        ok = false;
      }
    });

    // 🔥 Validar pattern desde schema_form
    schema.fields?.forEach(f => {
      if (f.pattern && formData[f.name]) {
        const regex = new RegExp(f.pattern);
        if (!regex.test(formData[f.name])) {
          errs[f.name] = f.patternMessage || 'Formato inválido';
          ok = false;
        }
      }
    });

    setErrors(errs);

    // 🔥 Focus al primer campo inválido
    if (!ok) {
      const primerCampoInvalido = Object.keys(errs)[0];
      setTimeout(() => {
        const el = document.querySelector(`[name="${primerCampoInvalido}"], input[data-field="${primerCampoInvalido}"]`);
        if (el) el.focus();
      }, 50);
    }

    return ok;
  };

  useEffect(() => {
    if (!schema || isDelete) return;

    if (isComponentMode) {
      setIsFormValid(totals.total > 0 && !hayFilasIncompletas());
      return;
    }
    
    const formIsValid = validateCompleteForm();
    const hasUniqueErrors = uniqueValidation.hasErrors;
    const isValidatingUnique = uniqueValidation.isValidating;
    
    // Validar que haya al menos una partida con importe > 0
    const hasValidDetails = useGenerico || (detailData && detailData.length > 0 && 
      detailData.some(d => parseFloat(d.imptotal || 0) > 0));
    
    // 🔥 NUEVO: Validar que NO haya acciones en progreso
    const noActionInProgress = !showConfirmaDelete && !hayFilasIncompletas();

    const canSave = isReadOnly
      ? false
      : isUpdate
        ? (formIsValid && !hasUniqueErrors && !isValidatingUnique && hasValidDetails && noActionInProgress && isDirty)
        : (formIsValid && !hasUniqueErrors && !isValidatingUnique && hasValidDetails && noActionInProgress);
    
    setIsFormValid(canSave);
  }, [formData, schema, isReadOnly, isDelete, uniqueValidation.errors, uniqueValidation.validating, detailData, showConfirmaDelete, totals]);

    const prevParamsRef = useRef({});

    useEffect(() => {
      if (!schema?.fields) return;
      if (isReadOnly) return;
      
      const dependentFields = schema.fields?.filter(
        f => f.type === 'select' && f.dataSource?.params
      ) || [];
      
      if (dependentFields.length === 0) return;
      
      dependentFields.forEach(async (field) => {
        const currentParams = field.dataSource.params.map(p => formData[p]).join('|');
        const prevParams = prevParamsRef.current[field.name];
        
        if (prevParams === currentParams) return;
        
        prevParamsRef.current[field.name] = currentParams;
        
        const hasAllParams = field.dataSource.params.every(
          paramName => formData[paramName] && formData[paramName] !== ''
        );
        
        if (hasAllParams) {

          const loadedOptions = await loadSelectOptions(field);
          
          const currentValue = formData[field.name];
          
          if (currentValue && loadedOptions.length > 0) {
            const valueExists = loadedOptions.some(
              opt => String(opt.value) === String(currentValue)
            );
            
            if (!valueExists) {
              setFormData(prev => ({ ...prev, [field.name]: '' }));
            }
          }
          
        } else {
          setSelectOptions(prev => ({ ...prev, [field.name]: [] }));          
          if (formData[field.name]) {
            setFormData(prev => ({ ...prev, [field.name]: '' }));
          }
        }
      });
    }, [formData, schema, isReadOnly]);

    const isInitialMount = useRef(true);

    useEffect(() => {
      if (isInitialMount.current && formData && Object.keys(formData).length > 0) {
        setTimeout(() => {
          isInitialMount.current = false;
        }, 0);
      }
    }, [formData]);

    useEffect(() => {

      const calculatedFields = schema?.fields?.filter(f => 
        f.calculated && f.formula
      ) || [];

      if (calculatedFields.length === 0) {
        return;
      }

      if ((isUpdate || isReadOnly) && isInitialMount.current) {
        const hasInitialValues = calculatedFields.every(field => 
          formData[field.name] !== undefined && 
          formData[field.name] !== null && 
          formData[field.name] !== 0
        );
        
        if (hasInitialValues) {
          return;
        }
      }

      if (!schema?.fields || Object.keys(formData).length === 0) {
        return;
      }

      const allFieldsLoaded = schema.fields.every(f => 
        formData.hasOwnProperty(f.name)
      );

      if (!allFieldsLoaded) {
        return;
      }

      const updates = {};
      
      calculatedFields.forEach(field => {

        let isDisabled = field.disabled;
        
        if (field.conditionalDisabled) {
          const { dependsOn, rules, defaultDisabled } = field.conditionalDisabled;
          const depValue = formData[dependsOn];
          const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));
          isDisabled = rule ? rule.disabled : (defaultDisabled ?? field.disabled);
        }

        if (!isDisabled) {
          return;
        }

        const formulaDeps = field.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
        
        const allDepsReady = formulaDeps.every(dep => 
          formData.hasOwnProperty(dep) && 
          formData[dep] !== undefined &&
          formData[dep] !== null &&
          formData[dep] !== ''
        );

        if (!allDepsReady) {
          return;
        }

        try {

          const formulaFunction = new Function(
            ...Object.keys(formData),
            `return ${field.formula}`
          );
          

          const values = Object.keys(formData).map(key => parseFloat(formData[key]) || 0);
          const result = formulaFunction(...values);
          
          const roundedResult = parseFloat(result.toFixed(field.decimals || 4));
          
          if (formData[field.name] !== roundedResult) {
            updates[field.name] = roundedResult;
          }
        } catch (error) {
          console.error(`❌ [CALC] Error calculando ${field.name}:`, error);
          console.error(`❌ [CALC] Formula: ${field.formula}`);
        }
      });

      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }

    }, [formData, schema, isUpdate, isReadOnly]);

    const handleChange = (name, value, field, event) => {
      setIsDirty(true);

      let val = (field?.format === 'uppercase' && typeof value === 'string') 
        ? value.toUpperCase() 
        : value;
      
      if (field?.sanitize && typeof val === 'string') {
        val = sanitizeOnType(val, field.sanitize);
      }
      
      if (formData[name] !== val) {

        const cursorPosition = event?.target?.selectionStart;
        
        setFormData(prev => ({ ...prev, [name]: val }));
        
        if (event?.target && cursorPosition !== null && cursorPosition !== undefined) {
          requestAnimationFrame(() => {
            if (event.target) {
              event.target.setSelectionRange(cursorPosition, cursorPosition);
            }
          });
        }
      }
      
      setTouched(prev => ({ ...prev, [name]: true }));

      if (field.unique) {
        uniqueValidation.validate(field, val);
      }

      if (field.type === 'number' && field.validation) {
        const numValue = parseFloat(val);
        let error = null;

        if (field.validation.min?.value) {
          const compareValue = parseFloat(formData[field.validation.min.value]);
          if (!isNaN(compareValue) && numValue < compareValue) {
            error = field.validation.min.message;
          }
        }

        if (!error && field.validation.greaterThan?.field) {
          const isValid = validateGreaterThan(numValue, field.validation.greaterThan.field, formData);
          if (!isValid) {
            error = field.validation.greaterThan.message;
          }
        }

        if (!error && field.validation.custom?.rule) {
          const tempData = { ...formData, [name]: val };
          const result = evaluateValidationRule(field.validation.custom.rule, tempData);
          if (!result) {
            error = field.validation.custom.message;
          }
        }

        if (error) {
          setErrors(prev => ({ ...prev, [name]: error }));
        } else {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[name];
            return newErrors;
          });
        }
      }
    };

  const handleBlur = (name, field) => {
    if (field?.sanitize && typeof formData[name] === 'string') {
      const sanitized = sanitizeOnBlur(formData[name], field.sanitize);
      if (sanitized !== formData[name]) {
        setFormData(prev => ({ ...prev, [name]: sanitized }));
      }
    }
    // 🔥 Validar pattern desde schema_form
    if (field?.pattern && formData[name]) {
      const regex = new RegExp(field.pattern);
      if (!regex.test(formData[name])) {
        setErrors(prev => ({ ...prev, [name]: field.patternMessage || 'Formato inválido' }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // 🔥 Procesar returnFields de select — universal
  const handleSelectReturnFields = (field, selectedValue) => {
    if (!field.dataSource?.returnFields?.length) return;

    // 🔥 restoreOnDefault — restaura valores previos al seleccionar defaultValue
    if (field.restoreOnDefault && (String(selectedValue) === String(field.defaultValue) || selectedValue === '')) {
      setFormData(prev => {
        const restored = {};
        if (field.restoreFields && field.restoreTargets && (isUpdate || isReadOnly)) {
          field.restoreTargets.forEach((target, idx) => {
            restored[target] = prev[field.restoreFields[idx]] ?? '';
          });
        } else {
          field.dataSource.returnFields.forEach(f => {
            restored[f] = preservedValues[f] ?? '';
          });
        }
        return { ...prev, ...restored };
      });
      return;
    }

    const selectedOption = selectOptions[field.name]?.find(
      opt => String(opt.value) === String(selectedValue)
    );
    if (selectedOption) {
      const extraFields = {};
      field.dataSource.returnFields.forEach(f => {
        extraFields[f] = selectedOption[f] ?? null;
      });
      setFormData(prev => ({ ...prev, ...extraFields }));
    }
  };

  const handleAddDetailRow = () => {
    if (!detailSchema) return;
    setIsDirty(true);
    
    const newRow = createEmptyDetailRow(detailSchema, detailData.length + 1);
    const newData = [...detailData, newRow];
    setDetailData(newData);
    
    setTimeout(() => {
      const newRowIndex = newData.length - 1;
      const codigoInput = document.querySelector(
        `input[data-row="${newRowIndex}"][data-field="codigo"]`
      );
      if (codigoInput) {
        codigoInput.focus();
      }
    }, 100);
  };

  const hayFilasIncompletas = () => {
    // 🔥 Si hay una celda con focus, hay edición en curso
    //if (focusedCell) return true; cambio 1
    
    return detailData.some(row => {
      const productIdField = detailSchema?.productIdField || 'idinvearts';
      const codeField = detailSchema?.codeField || 'codigo';
      const quantityField = detailSchema?.quantityField || 'cantidad';
      const priceField = detailSchema?.priceField || 'precio';
      
      const hasProduct = row[productIdField] && row[productIdField] > 0;
      const hasCode = row[codeField] && String(row[codeField]).trim() !== '';
      const hasQuantity = row[quantityField] && parseFloat(row[quantityField]) > 0;
      const hasPrice = row[priceField] && parseFloat(row[priceField]) > 0;
      
      // 🔥 NUEVO: Si NO tiene producto, es incompleta
      if (!hasProduct) return true;
      
      // 🔥 Si tiene producto pero no está completa
      const hasAnything = hasCode || hasProduct;
      const isComplete = hasProduct && hasQuantity && hasPrice && row.partida_completa === true;
      
      return hasAnything && !isComplete;
    });
  };

  const hayMasterFieldsFaltantes = () => {
    const requiredFields = detailSchema?.requiresMasterFields || [];
    return requiredFields.some(rule => {
      const valor = formData[rule.field];
      return !valor || valor === '' || valor === 0 || valor === '0';
    });
  };
  
  const getMasterFieldMensaje = () => {
    const requiredFields = detailSchema?.requiresMasterFields || [];
    const faltante = requiredFields.find(rule => {
      const valor = formData[rule.field];
      return !valor || valor === '' || valor === 0 || valor === '0';
    });
    return faltante || null;
  };

  /**
   * Elimina partida SIN confirmación (ESC, valor 0, etc)
   */
  const handleDeleteDetailRow = (rowIndex) => {
    if (isUpdate && !isComponentMode) {
      // ← solo bloquear si es partida existente
      const row = detailData[rowIndex];
      if (row?._id?.startsWith('existing_')) return;
    }
    
    if (!detailSchema) return;
    
    const minRows = detailSchema.minRows || 0;
    if (detailData.length <= minRows) {
      toast.warning(`Debe haber al menos ${minRows} partida(s)`);
      return;
    }

    // Salvaguarda: si la fila se elimina en vez de completarse, el flag de
    // handleEditDetailRow nunca llega a limpiarse por su ruta normal (el
    // bloque completeRow). Hoy isDetailRowMenuEnabled ya bloquea "Eliminar"
    // mientras hay una fila incompleta, asi que esta ruta no deberia
    // alcanzarse con el flag en true -- se limpia aqui de todos modos como
    // seguro adicional, sin costo.
    isReeditingDetailRowRef.current = false;

    setIsDeleting(true);

    setFocusedCell(null);
    setActiveTooltip(null);
    setTooltipPosition({ top: 0, left: 0 });

    const newData = detailData.filter((_, idx) => idx !== rowIndex);

    newData.forEach((row, idx) => {
      row.numero = idx + 1;
    });

    setDetailData(newData);

    setTimeout(() => {
      setIsDeleting(false);

      const buttons = document.querySelectorAll('button[type="button"]');
      const addButton = Array.from(buttons).find(btn =>
        btn.textContent.trim().includes('+ Agregar Partida')
      );
      if (addButton) {
        addButton.focus();
      }
    }, 100);
  };

  /**
   * Abre modal de confirmación (solo desde menú Acciones)
   */
  const handleDeleteDetailRowWithConfirm = (rowIndex) => {
    if (!detailSchema) return;
    
    const minRows = detailSchema.minRows || 0;
    if (detailData.length <= minRows) {
      toast.warning(`Debe haber al menos ${minRows} partida(s)`);
      return;
    }
    
    setRowToDelete(rowIndex);
    setShowConfirmaDelete(true);
  };

  // ==========================================
  // HANDLERS: Acciones del menú Detail Grid
  // ==========================================

  /**
   * Ejecuta eliminación después de confirmar en modal
   */
  const confirmDeleteDetailRow = () => {
    if (rowToDelete === null) return;

    // Misma salvaguarda que handleDeleteDetailRow -- ver comentario ahí.
    isReeditingDetailRowRef.current = false;

    setIsDirty(true);
    setIsDeleting(true);

    setFocusedCell(null);
    setActiveTooltip(null);
    setTooltipPosition({ top: 0, left: 0 });
    
    const newData = detailData.filter((_, idx) => idx !== rowToDelete);
    
    newData.forEach((row, idx) => {
      row.numero = idx + 1;
    });
    
    setDetailData(newData);
    
    toast.success('✅ Partida eliminada');
    
    // Limpiar estados
    setShowConfirmaDelete(false);
    setRowToDelete(null);
    
    setTimeout(() => {
      setIsDeleting(false);
      
      const buttons = document.querySelectorAll('button[type="button"]');
      const addButton = Array.from(buttons).find(btn => 
        btn.textContent.trim().includes('+ Agregar Partida')
      );
      if (addButton) {
        addButton.focus();
      }
    }, 100);
  };

  /**
   * Handler: Editar partida completa
   * Cambia partida_completa a false para habilitar campos
   */
  const handleEditDetailRow = (rowIndex, row) => {
    isReeditingDetailRowRef.current = true;

    const newData = [...detailData];
    newData[rowIndex].partida_completa = false;
    setDetailData(newData);

    toast.info('💡 Partida habilitada para edición');
    
    // Focus automático en Cantidad
    setTimeout(() => {
      const cantidadInput = document.querySelector(
        `input[data-row="${rowIndex}"][data-field="cantidad"]`
      );
      if (cantidadInput) {
        cantidadInput.focus();
        cantidadInput.select();
      }
    }, 100);
  };

  /**
   * Dispatcher: Ejecuta acción según key del menú
   */
  const handleDetailAccionClick = (accion, row) => {
    const rowIndex = detailData.findIndex(r => r._id === row._id);
    
    if (rowIndex === -1) {
      toast.error('Error: fila no encontrada');
      return;
    }
    
    if (accion.key === 'edit') {
      handleEditDetailRow(rowIndex, row);
    } else if (accion.key === 'delete') {
      handleDeleteDetailRowWithConfirm(rowIndex);  // ✅ NUEVA FUNCIÓN
    }
  };

  /**
   * Genera acciones disponibles para el menú
   * Siempre retorna Edit + Delete
   */
  const getDetailRowAcciones = (row) => {
    const acciones = [];
    
    acciones.push({
      key: 'edit',
      icon: 'edit',
      color: 'blue',
      label: 'Editar'
    });
    
    acciones.push({
      key: 'delete',
      icon: 'trash',
      color: 'red',
      label: 'Eliminar'
    });
    
    return acciones;
  };

  /**
   * 🔥 Valida si el menú debe estar habilitado
   * Deshabilita TODOS los menús si hay alguna partida en edición
   */
  const isDetailRowMenuEnabled = (row, rowIndex) => {
    // REGLA 1: Si hay CUALQUIER celda con focus → DESACTIVAR TODAS
    if (focusedCell) {
      return false;
    }
    
    // REGLA 2: Si hay filas incompletas → DESACTIVAR TODAS
    if (hayFilasIncompletas()) {
      return false;
    }
    
    // REGLA 3: Si la partida NO está completa → DESACTIVAR
    if (row.partida_completa !== true) {
      return false;
    }
    
    // REGLA 4: Si NO tiene producto → DESACTIVAR
    const productIdField = detailSchema?.productIdField || 'idinvearts';
    const hasProduct = row[productIdField] && row[productIdField] > 0;
    
    if (!hasProduct) {
      return false;
    }
    
    // ✅ Solo si NINGUNA partida está en edición → ACTIVAR
    return true;
  };

  const validateDetailField = (col, value, rowIndex) => {
    if (!col.validation?.positive) return true;
    const numValue = parseFloat(value) || 0;
    if (numValue < 0) {
      toast.error(`⚠️ ${col.label} negativo no permitido. Partida eliminada.`);
      handleDeleteDetailRow(rowIndex);
      setTimeout(() => {
        const buttons = document.querySelectorAll('button[type="button"]');
        const addButton = Array.from(buttons).find(btn => 
          btn.textContent.includes('+ Agregar Partida')
        );
        if (addButton) {
          addButton.focus();
        }
      }, 100);
      return false;
    }
    if (numValue === 0) {
      toast.warning(`${col.label} debe ser mayor a 0. Partida eliminada.`);
      handleDeleteDetailRow(rowIndex);
      setTimeout(() => {
        const buttons = document.querySelectorAll('button[type="button"]');
        const addButton = Array.from(buttons).find(btn => 
          btn.textContent.includes('+ Agregar Partida')
        );
        if (addButton) {
          addButton.focus();
        }
      }, 100);
      return false;
    }
    return true;
  };

  const isDetailFieldReadOnly = (col, row) => {
        
    if (col.disabledIf && row[col.disabledIf.field] === col.disabledIf.value) return true;
    if (isComponentMode && col.componentOnly) return false;

    if (!col.editable) return true;
    const productIdField = detailSchema?.productIdField || 'idinvearts';
    const descriptionField = detailSchema?.descriptionField || 'producto';
    const codeField = detailSchema?.codeField || 'codigo';
    const quantityField = detailSchema?.quantityField || 'cantidad';
    const priceField = detailSchema?.priceField || 'precio';
    const hasProduct = row[productIdField] && row[productIdField] > 0;

    if (col.name === descriptionField) return true;
    if (col.name === codeField && hasProduct) return true;
    if (col.name === quantityField || col.name === priceField) {
      if (!hasProduct) return true;
      if (row.partida_completa === true) return true;
      if (col.name === priceField && row.prelibre === false && userPermissionLevel !== 'full') return true;
    }

    // isDetailFieldReadOnly — pordesc
    if (col.name === 'pordesc') {

      //console.log('🔍 EDIT pordesc check:', {
      //  descventas: formData?.descventas,
      //  descuento_maximo_permitido: formData?.descuento_maximo_permitido,
      //  topeMax: parseFloat(formData?.descuento_maximo_permitido) || 0
      //});
      
      //console.log('🔍 descuento_maximo_permitido raw:', 
      //  JSON.stringify(formData?.descuento_maximo_permitido)
      //);

      //console.log('🔍 pordesc check:', {
      //  descventas: formData?.descventas,
      //  descventas_type: typeof formData?.descventas,
      //  clientePermite: formData?.descventas === true,
      //  productoPermite: row.descventas === true,
      //  topeMax: parseFloat(formData?.descuento_maximo_permitido) || 0,
      //  descuento_maximo_permitido: formData?.descuento_maximo_permitido
      //});

      if (!hasProduct) return true;
      if (row.partida_completa === true) return true;
      const topeMax = parseFloat(formData?.descuento_maximo_permitido) || 0;
      if (topeMax <= 0) return true; // constante apaga feature
      const clientePermite = formData?.descventas === true;
      const productoPermite = row.descventas === true;
      if (!clientePermite || !productoPermite) return true;
    }

    return false;
  };

  const updateDetailCellValue = (rowIndex, fieldName, value) => {
    setIsDirty(true);
    const newData = [...detailData];
    newData[rowIndex][fieldName] = value;
    const col = detailSchema?.columns?.find(c => c.name === fieldName);
    if (col?.recalculateOnChange) {

      // console.log('🔍 updateDetailCellValue:', {
      //   fieldName,
      //   value,
      //   surtida: newData[rowIndex].surtida,
      //   cantidad: newData[rowIndex].cantidad,
      //   cotizacion_esbase: formData.cotizacion_esbase,
      //   tipocamb_valor: formData.tipocamb_valor
      // });

      
      const totalesPartida = calcularTotalesPartida(
        newData[rowIndex],
        formData.cotizacion_esbase ?? true,
        formData.tipocamb_valor ?? 1,
        isComponentMode ? parseFloat(newData[rowIndex].surtida) || 0 : null,
        formData.c_regimenfiscal ?? '000'
      );

      newData[rowIndex].imptotal = totalesPartida.imptotal;
      newData[rowIndex].impiva = totalesPartida.impiva;
      newData[rowIndex].impdesc = totalesPartida.impdesc;
      newData[rowIndex].importe_bruto = totalesPartida.importe_bruto;
      newData[rowIndex].total = totalesPartida.imptotal;
      newData[rowIndex].impuestos_extra  = totalesPartida.impuestos_extra;
    }
    setDetailData(newData);
  };

  /**
   * Muestra tooltip dinámico (estilo VFP GotFocus/LostFocus)
   * @param {string} text - Texto del tooltip (vacío para ocultar)
   * @param {object} element - Elemento input para calcular posición
   */
  const showTooltip = (text, element) => {
    // 🔥 NUEVO: Si estamos eliminando, NO mostrar tooltips
    if (isDeleting) {
      setActiveTooltip(null);
      return;
    }
    
    if (!text || !element) {
      // LostFocus: Destruir tooltip
      setActiveTooltip(null);
      return;
    }
    
    // GotFocus: Crear tooltip en posición calculada
    const rect = element.getBoundingClientRect();

    // 🔥 Posición ABAJO del campo
    let top = rect.bottom + window.scrollY + 8;  // 8px debajo del campo
    let left = rect.left + window.scrollX;
    
    // 🔥 Validación: Si se sale por la derecha
    const tooltipWidth = 450; // Ancho aproximado del tooltip
    if (left + tooltipWidth > window.innerWidth) {
      left = window.innerWidth - tooltipWidth - 20;
    }
    
    // 🔥 Validación: Si se sale por la izquierda
    if (left < 10) {
      left = 10;
    }
    
    // 🔥 Validación: Si se sale por abajo (raro, pero por si acaso)
    const tooltipHeight = 40;
    if (rect.bottom + tooltipHeight + 20 > window.innerHeight) {
      // Si no cabe abajo, ponerlo ARRIBA
      top = rect.top + window.scrollY - tooltipHeight - 8;
    }
    
    setTooltipPosition({ top, left });      
    setActiveTooltip(text);
  };

  const handleDetailCellChange = async (rowIndex, fieldName, value) => {
    const newData = [...detailData];
    //newData[rowIndex][fieldName] = value;
    
    const column = detailSchema?.columns?.find(col => col.name === fieldName);
    
    if (column?.searchable && value && String(value).trim() !== '') {
      const producto = await buscarProducto(value, 'byId');
      
      if (producto) {
        // ✅ Producto encontrado - Liberar bloqueo
        setIsDirty(true);
        setInvalidCodigoRow(null);
        
        const fieldMapping = detailSchema?.fieldMapping || {};
        
        Object.entries(fieldMapping).forEach(([destino, origen]) => {
          newData[rowIndex][destino] = producto[origen];
        });

        // 🔥 Validar TC — si monedas difieren y no hay TC → eliminar partida
        const producto_esbase = producto.esbase ?? true;
        const cotizacion_esbase = formData.cotizacion_esbase ?? true;
        const tcFormData = parseFloat(formData.tipocamb_valor) || 0;
        const tc = tcFormData > 1 
          ? tcFormData 
          : parseFloat(selectOptions['idsatmoneda']?.find(opt => !opt.cotizacion_esbase)?.tipocamb_valor) || 1;

        //console.log('🔍 TC DEBUG:', {
        //  tc,
        //  tipocamb_valor: formData.tipocamb_valor,
        //  selectOptions_idsatmoneda: selectOptions['idsatmoneda'],
        //  find_result: selectOptions['idsatmoneda']?.find(opt => !opt.cotizacion_esbase)
        //});

        if (producto_esbase !== cotizacion_esbase && tc <= 1) {
          toast.error('❌ El tipo de cambio es requerido para el producto.');
          handleDeleteDetailRow(rowIndex);
          return;
        }

        // 🔍 DIAGNÓSTICO COMPLETO RETORNA TODOS LOS CAMPOS AL CAPTURAR ID/CODIGO DE BARRAS/BUSQUEDA DE PRODUCTOS
        // console.log('🔍 ============ PRODUCTO CARGADO ============');
        // console.log('🔍 Producto RAW completo:', JSON.stringify(producto, null, 2));
        // console.log('🔍 Row completo después de fieldMapping:', JSON.stringify(newData[rowIndex], null, 2));
        // console.log('🔍 ==========================================');
        
        if (!newData[rowIndex].cantidad || newData[rowIndex].cantidad === 0) {
          newData[rowIndex].cantidad = 1;
        }

        //console.log('🔍 ANTES calcular:', {
        //  precio: newData[rowIndex].precio,
        //  cantidad: newData[rowIndex].cantidad,
        //  cotizacion_esbase: formData.cotizacion_esbase,
        //  tipocamb_valor: formData.tipocamb_valor,
        //  esbase: newData[rowIndex].esbase
        //});

        // 🔥 Convertir precio si monedas difieren
        if (producto_esbase !== cotizacion_esbase && tc > 1) {
          newData[rowIndex].precio = cotizacion_esbase
            ? parseFloat((parseFloat(newData[rowIndex].precio) * tc).toFixed(4))
            : parseFloat((parseFloat(newData[rowIndex].precio) / tc).toFixed(4));
          setFormData(prev => ({ ...prev, tipocambio: parseFloat(tc) }));
        }

        const totalesPartida = calcularTotalesPartida(
          newData[rowIndex],
          formData.cotizacion_esbase ?? true, 
          formData.tipocamb_valor ?? 1, 
          null, 
          formData.c_regimenfiscal ?? '000'
        );

        newData[rowIndex].imptotal = totalesPartida.imptotal;
        newData[rowIndex].impiva = totalesPartida.impiva;
        newData[rowIndex].impdesc = totalesPartida.impdesc;
        newData[rowIndex].importe_bruto = totalesPartida.importe_bruto;
        newData[rowIndex].total = totalesPartida.imptotal;
        newData[rowIndex].impuestos_extra = totalesPartida.impuestos_extra;
        
        setDetailData(newData);
        
        toast.success(`Producto cargado: ${producto.producto}`);

        // ✅ Focus a Cantidad después de cargar producto
        setTimeout(() => {
          const cantidadInput = document.querySelector(
            `input[data-row="${rowIndex}"][data-field="cantidad"]`
          );
          if (cantidadInput) {
            cantidadInput.focus();
            cantidadInput.select();
          }
        }, 100);

      } else {
        // ❌ Producto NO encontrado
        // 🔥 OPCIÓN A: Limpiar campo
        newData[rowIndex][fieldName] = '';
        setDetailData(newData);
        
        toast.error('❌ Producto no encontrado. Verifique el código.');
        
        // 🔥 OPCIÓN A: Refocus al mismo campo limpio
        setTimeout(() => {
          const codigoInput = document.querySelector(
            `input[data-row="${rowIndex}"][data-field="${fieldName}"]`
          );
          if (codigoInput) {
            codigoInput.focus();
            codigoInput.select();
          }
        }, 100);
        
        return; // ⚠️ Salir sin ejecutar setDetailData al final
      }
    }
    
    setDetailData(newData);
  };

  const buscarProducto = async (valor, searchType = 'byId') => {
    if (!valor || String(valor).trim() === '') return null;
    
    const token = sessionStorage.getItem('mapasac_access_token');
    let valorLimpio = String(valor).trim();
    
    try {
      const searchMethod = detailSchema?.searchMethods?.[searchType];      
      if (!searchMethod || !searchMethod.enabled) {
        return null;
      }
      if (searchMethod.cleanLeadingZeros && valorLimpio.charAt(0) === '0') {
        valorLimpio = valorLimpio.substring(1);
      }
      
      let param = valorLimpio;
      
      if (searchType === 'byId') {
        param = parseInt(valorLimpio);
        if (isNaN(param)) {
          return null;
        }
      }
      
      const query = searchMethod.query;
      
      const response = await secureFetch('/api/sqlexec_generico/query-params', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query, params: [param] })
      });
      
      const result = await response.json();
      
      if (result?.success && result?.data?.length === 1) {
        return result.data[0];
      }
      
      if (searchType === 'byId' && detailSchema?.searchMethods?.byBarcode?.enabled) {
        const barcodeConfig = detailSchema.searchMethods.byBarcode;
        const minLength = barcodeConfig.minLength || 8;
        
        if (valorLimpio.length >= minLength) {
          return await buscarProducto(valor, 'byBarcode');        
        }
      }
      
      return null;
      
    } catch (error) {
      console.error('❌ Error buscando producto:', error);
      toast.error('Error buscando producto');
      return null;
    }
  };

  // ============================================
  // MOTOR DE CÁLCULOS TOTALES DESDE JSON
  // ============================================
  const ejecutarMotorCalculos = useCallback((partidas = detailData) => {

    if (!schema?.sqlexec_calculos || partidas.length === 0) {
      if (isCreate) {
        setTotals({
          importe: 0, descuento: 0, subtotal: 0, iva: 0,
          subtotal_con_iva: 0, ieps: 0, retencion_iva: 0,
          retencion_isr: 0, total: 0
        });
      }
      return;
    }

    const calculos = schema.sqlexec_calculos
      .filter(c => c.activo)
      .sort((a, b) => a.orden_ejecucion - b.orden_ejecucion);

    const resultado = {};

    for (const calc of calculos) {
      const { campo_destino, tipo_calculo, configuracion } = calc;
      let valor = 0;

      switch (tipo_calculo) {
        case 'suma_detalle':
          valor = partidas.reduce((sum, partida) => {
            const valorPartida = parseFloat(partida[configuracion.origen.campo]) || 0;
            return sum + valorPartida;
          }, 0);
          break;
        case 'formula_master':
          // Evaluar fórmula con variables
          try {
            let expresion = configuracion.expresion;
            Object.keys(configuracion.variables).forEach(variable => {
              const path = configuracion.variables[variable];
              const valorVar = path.startsWith('campo.')
                ? resultado[path.replace('campo.', '')]
                : 0;
              expresion = expresion.replace(`{${variable}}`, valorVar);
            });
            valor = Function('return ' + expresion)();
          } catch (error) {
            console.error('Error en fórmula:', error);
          }
          break;
        case 'suma_detalle_extra':
          valor = partidas.reduce((sum, partida) => {
            const extras = Array.isArray(partida.impuestos_extra) ? partida.impuestos_extra : [];
            const filtro = configuracion.filtro || {};
            
            const filtradas = extras.filter(tasa => {
              if (filtro.traslado !== undefined && tasa.traslado !== filtro.traslado) return false;
              if (filtro.retencion !== undefined && tasa.retencion !== filtro.retencion) return false;
              if (filtro.contiene && !tasa.descripcio?.toUpperCase().includes(filtro.contiene.toUpperCase())) return false;
              if (filtro.nodo_xml !== undefined && tasa.nodo_xml !== filtro.nodo_xml) return false;
              return true;
            });

            return sum + filtradas.reduce((s, t) => {
              // ← Si no tiene importe, calcularlo desde base de la partida
              const importe = t.importe !== undefined
                ? parseFloat(t.importe) || 0
                : parseFloat((parseFloat(partida.imptotal) * parseFloat(t.tasaocuota)).toFixed(4));
              return s + importe;
            }, 0);

          }, 0);
          break;
        default:
          console.warn(`⚠️ Tipo de cálculo desconocido: ${tipo_calculo}`);
          valor = 0;
          break;
      }
      resultado[campo_destino] = parseFloat(valor.toFixed(configuracion.redondeo || 2));
    }
    setTotals(prev => ({ ...prev, ...resultado }));
  }, [schema, detailData]);

  // 🔥 AGREGAR AQUÍ EL useEffect NUEVO
  useEffect(() => {
    ejecutarMotorCalculos(detailData);
  }, [detailData, ejecutarMotorCalculos]);

  const handleReloadFreshData = async () => {
    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    try {
      setLoading(true);
      const token = sessionStorage.getItem('mapasac_access_token');
      const id = initialData[idField];

      const r = await secureFetch(`/api/sqlexec/${componentForm}/read/${id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      const j = await r.json();
      if (j?.success && j?.data) {
        const freshData = {};
        schema.fields?.forEach(f => {
          freshData[f.name] = f.type === 'select'
            ? String(j.data[f.name] || '')
            : (j.data[f.name] ?? '');
        });

        setFormData(freshData);
        setOriginalTimestamp(j.data.actualizada);
        setConflictData(null);
        setShowConflictModal(false);
        toast.success('Datos actualizados');
      }
    } catch (e) {

      logComponentError('SqlexecMasterDetail', 'reload_fresh_data', 
        `${componentForm} - ${e.message}`);

      toast.error('Error recargando datos desde el schema (F)');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(async () => {
    if (mode === 'create' || !idField || !initialData?.[idField]) {
      onClose?.();
      return;
    }

    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const id = initialData[idField];

      const r = await secureFetch(`/api/sqlexec/${componentForm}/read/${id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (r.status === 404) {
        toast.error('❌ Este registro fue eliminado por otro usuario');
        onSuccess?.();
        setTimeout(() => onClose?.(), 100);
        return;
      }
      
      onClose?.();

    } catch (error) {
      console.error('Error validando existencia:', error);
      onClose?.();
    }
  }, [mode, idField, initialData, componentForm, onClose, onSuccess]);

  const handleGenericoSubmit = async () => {
    if (schema?.submitAction === 'confirm_only') {
      onSuccess?.();
      onClose?.();
      return;
    }
    if (schema?.submitAction === 'return_data') {
      onSuccess?.(formData);
      onClose?.();
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      await sendEmail({
        idreporte,
        id: formData[idField],
        para: formData.email,
        cc: formData.cc,
        asunto: formData.asunto,
        mensaje: formData.mensaje,
        adjuntos: (formData.adjuntos || []).filter(a => !a.esDefault),
        servicio_clave: servicioClave || 'EMAIL',
        async: asyncEmail || false
      }, token);
      toast.success('✅ Email enviado correctamente');
      onSuccess?.();
      onClose?.();
    } catch (e) {
      // Detectar "sin saldo" por contenido (mismo patron que MAPASAC-INV-000)
      // -- este catch cubre CUALQUIER error de sendEmail(), no solo saldo, asi
      // que el titulo especifico solo aplica cuando el mensaje real lo es.
      // SERVICIO_NO_CONFIGURADO (409): el tenant no tiene fila en
      // mapasoft.servicios para EMAILS -- config administrativa faltante,
      // distinto de "sin saldo". Se detecta por code (lo propaga sendEmail).
      const errMsg = e.message || 'Error enviando email';
      if (e.code === 'SERVICIO_NO_CONFIGURADO') {
        // Mismo formato estandar que cliepara/invepara: titulo + cuerpo +
        // pasos numerados + info tecnica.
        showMensajeModal('error', 'Servicio de correos no configurado',
          'El servicio de envío de correos no está configurado para tu empresa. Contacte al administrador para configurar.',
          [
            "Verifica que exista una fila en mapasoft.servicios para el tenant_id de esta empresa con clave='EMAILS'",
            'Si no existe, un administrador debe crearla con saldo y utilizado'
          ],
          { component_form: componentForm, tabla: 'mapasoft.servicios' });
      } else {
        const tituloError = errMsg.includes('Saldo insuficiente')
          ? 'Saldo insuficiente de correos'
          : 'Error al enviar el correo';
        showMensajeModal('error', tituloError, errMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const executeSubmit = async () => {
    if (useGenerico) {
      await handleGenericoSubmit();
    } else {
      const fakeEvent = { preventDefault: () => {} };
      await handleSubmit(fakeEvent);
      setShowConfirmaMensaje(false);  // Cerrar modal después de ejecutar
    }
  };

  const ejecutarRemision = async (dataToSave, details) => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');

      const masterData = { ...dataToSave };

      const payload = {
        master: masterData,
        details: details.filter(row => parseFloat(row.surtida) > 0)
      };

      // ← Leer dinámicamente desde el schema, zero hardcode
      const targetComponent = schema?.config?.create_on_update;

      if (!targetComponent) {
        toast.error('Componente destino no configurado (create_on_update)');
        setSaving(false);
        return;
      }

      const r = await secureFetch(
        `/api/sqlexec_master_detail/${targetComponent}/create`,
        {
          method: 'POST',
          headers: { 
            Authorization: `Bearer ${token}`, 
            'Content-Type': 'application/json' 
          },
          body: JSON.stringify(payload)
        }
      );
      const j = await r.json();
      if (j?.success) {
        const exitoMensaje = schema?.confirm?.exitoMensajeSurtir;
        if (exitoMensaje) {
          const record = j.data?.data || j.data;
          showMensajeModal('success', schema?.confirm?.exitoTitulo || 'Operación exitosa',
            resolvePlaceholders(exitoMensaje, { ...dataToSave, ...(record && typeof record === 'object' ? record : {}) }),
            [], null, () => {
              onSuccess?.();
              onClose?.();
            });
        } else {
          toast.success('✅ Remisión generada correctamente');
          onSuccess?.();
          onClose?.();
        }
      } else {
        const errMsg = j?.error?.message || j?.message || 'Error al generar remisión';

        if (errMsg.includes('MAPASAC-INV-000')) {
          let sucursalLabel = '';
          try {
            const session = JSON.parse(sessionStorage.getItem('mapasac_session'));
            sucursalLabel = session?.empresa?.nombre || `#${session?.user?.idsucursal ?? ''}`;
          } catch { /* noop */ }

          showMensajeModal(
            'error',
            'Sucursal sin parámetros de inventario configurados',
            `No se puede generar la remisión: la sucursal ${sucursalLabel} no tiene parámetros de inventario configurados en invepara. Contacte al administrador para configurar.`,
            [
              'Verifica que exista una fila en mapasoft.invepara para el idsucursal de esta sucursal',
              'Si no existe, un administrador debe crearla con el valor de "inventario" correspondiente'
            ],
            { component_form: componentForm, tabla: 'mapasoft.invepara', detalle_tecnico: errMsg }
          );
        } else {
          toast.error(errMsg);
        }
      }
    } catch (e) {
      console.error('❌ Error remisión:', e);
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {

    //console.log('🔍 [STEP 3] handleSubmit - surtirMode:', surtirMode);

    e.preventDefault();
    if (isDelete) return handleDelete();

    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    if (!isReadOnly && !validateCompleteForm()) {
      setHeaderCollapsed(false); // collapsibleHeader: que se vean los mensajes
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    for (const field of schema?.fields || []) {
      if (field.type === 'number' && formData.hasOwnProperty(field.name)) {
        const value = parseFloat(formData[field.name]);
        
        if (field.min !== undefined && value < field.min) {
          setHeaderCollapsed(false);
          toast.error(`${field.label} debe ser mayor o igual a ${field.min}`);
          return;
        }
        
        if (field.max !== undefined && value > field.max) {
          setHeaderCollapsed(false);
          toast.error(`${field.label} debe ser menor o igual a ${field.max}`);
          return;
        }

        if (field.validation?.min?.value) {
          const compareField = field.validation.min.value;
          const compareValue = parseFloat(formData[compareField]);
          
          if (!isNaN(compareValue) && value < compareValue) {
            const msg = field.validation.min.message || 
              `${field.label} debe ser mayor o igual a ${schema.fields.find(f => f.name === compareField)?.label}`;
            setHeaderCollapsed(false);
            toast.error(msg);
            return;
          }
        }

        if (field.validation?.greaterThan?.field) {
          const isValid = validateGreaterThan(value, field.validation.greaterThan.field, formData);
          
          if (!isValid) {
            const msg = field.validation.greaterThan.message || 
              `${field.label} debe be mayor que ${schema.fields.find(f => f.name === field.validation.greaterThan.field)?.label}`;
            setHeaderCollapsed(false);
            toast.error(msg);
            return;
          }
        }

        if (field.validation?.custom?.rule) {
          const result = evaluateValidationRule(field.validation.custom.rule, formData);
          
          if (!result) {
            setHeaderCollapsed(false);
            toast.error(field.validation.custom.message || `${field.label} no cumple la validación`);
            return;
          }
        }
      }
    }

    // Validar que haya al menos una partida
    if (!detailData || detailData.length === 0) {
      toast.error('⚠️ Debe agregar al menos una partida');
      return;
    }

    // Validar que haya al menos una partida con importe > 0
    //const sumImportes = detailData.reduce((sum, d) => sum + (parseFloat(d.imptotal) || 0), 0);
    //if (sumImportes <= 0) {
    //  toast.error('⚠️ Debe agregar al menos una partida con importe mayor a cero');
    //  return;
    //}
    const sumImportes = detailData.reduce((sum, d) => sum + (parseFloat(d.imptotal) || 0), 0);
    const sumImporteBruto = detailData.reduce((sum, d) => sum + (parseFloat(d.importe_bruto) || 0), 0);
    if (sumImportes <= 0 || sumImporteBruto <= 0) {
      toast.error('⚠️ Debe agregar al menos una partida con importe mayor a cero');
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');
      const endpoint = isCreate
        ? `/api/sqlexec_master_detail/${componentForm}/create`
        : `/api/sqlexec_master_detail/${componentForm}/update`;

      const dataToSave = {};

      const isFieldDisabled = (field) => {
        if (field.conditionalDisabled) {
          const { dependsOn, rules, defaultDisabled } = field.conditionalDisabled;
          const depValue = formData[dependsOn];
          
          const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));
          
          if (rule) {
            return rule.disabled;
          }
          
          return defaultDisabled ?? field.disabled;
        }
        
        return field.disabled;
      };

      const safeVal = (val, fieldName) => {
        if (val === null || val === undefined) {
          return (schema?.config?.nullable_fks || []).includes(fieldName) ? null : '';
        }
        return val;
      };

      schema.fields?.forEach(field => {
        if (field.virtual) return;
        if (field.system === true || field.source === 'jwt') return;
        
        const realDisabled = isFieldDisabled(field);
        
        if (field.type !== 'hidden' && !realDisabled && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = safeVal(formData[field.name], field.name);
        }
        
        if (field.calculated && realDisabled && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = safeVal(formData[field.name], field.name);
        }
        
        if (field.type === 'hidden' && !field.param && formData.hasOwnProperty(field.name)) {
          dataToSave[field.name] = safeVal(formData[field.name], field.name);
        }
      });

      // 1. Eliminar PK si está vacío (se genera automático)
      if (dataToSave.idcliecoen === "" || dataToSave.idcliecoen === null) {
        delete dataToSave.idcliecoen;
      }

      // 2. Convertir "0" a null para nullable FKs
      const nullableFKs = schema?.config?.nullable_fks || [];
      nullableFKs.forEach(fkName => {
        if (dataToSave[fkName] === "0" || dataToSave[fkName] === 0 || dataToSave[fkName] === "") {
          dataToSave[fkName] = null;
        }
      });

      // ✅ Regla negocio: tipocambio según moneda de partidas
      const monedas = [...new Set((detailData || []).map(d => d.idsatmoneda))];
      dataToSave.tipocambio = monedas.length > 1
        ? parseFloat(dataToSave.tipocambio) || null
        : null;

      // 🔥 Incluir campos date disabled — calculados en frontend
      schema.fields?.forEach(field => {
        if ((field.type === 'date' || (field.type === 'number' && !field.calculated))
            && field.disabled
            && formData.hasOwnProperty(field.name)
            && !dataToSave.hasOwnProperty(field.name)) {
            dataToSave[field.name] = formData[field.name];
        }
      });

      // ✅ NUEVO: Calcular totales desde details
      if (detailData && detailData.length > 0) {
        dataToSave.impsub    = Number((totals.subtotal    || 0).toFixed(4));
        dataToSave.impdesc   = Number((totals.descuento   || 0).toFixed(4));
        dataToSave.impieps   = Number((totals.ieps        || 0).toFixed(4));
        dataToSave.impiva    = Number((totals.iva         || 0).toFixed(4));
        dataToSave.impretiva = Number((totals.retencion_iva || 0).toFixed(4));
        dataToSave.impretisr = Number((totals.retencion_isr || 0).toFixed(4));
        dataToSave.impotros  = Number((totals.otros_traslados || 0).toFixed(4));
        dataToSave.imptot    = Number((totals.total       || 0).toFixed(4));
      } else {
        dataToSave.impsub    = 0;
        dataToSave.impdesc   = 0;
        dataToSave.impieps   = 0;
        dataToSave.impiva    = 0;
        dataToSave.impretiva = 0;
        dataToSave.impretisr = 0;
        dataToSave.impotros  = 0;
        dataToSave.imptot    = 0;
      }

      // ✅ surtirMode — disparar remisión con dataToSave completo
      if (surtirMode) {
        const partidas = detailData.filter(row => parseFloat(row.surtida) > 0);
        if (partidas.length === 0) {
          toast.error('⚠️ Debe capturar al menos una cantidad surtida');
          setSaving(false);
          return;
        }
        await ejecutarRemision(dataToSave, detailData);
        return;
      }

      //console.log('🔍 dataToSave ANTES payload:', JSON.stringify(dataToSave));

      // ✅ NUEVO: Payload con master y details

      //console.log('🔍 impuestos_extra al guardar:', 
      //  JSON.stringify(detailData.map(d => ({ 
      //    producto: d.producto, 
      //    impuestos_extra: d.impuestos_extra 
      //  })))
      //);

      const payload = {
        master: isUpdate
          ? { 
              ...dataToSave,
              [idField]: initialData[idField],
              _original_updated_at: originalTimestamp
            }
          : dataToSave,
        details: detailData || []
      };

      //console.log('🔍 [1] PAYLOAD COMPLETO:', payload);
      //console.log('🔍 [2] MASTER:', payload.master);
      //console.log('🔍 [3] DETAILS COUNT:', payload.details?.length);
      //console.log('🔍 [4] PRIMERA PARTIDA:', payload.details?.[0]);
      //console.log('🔍 [5] TIENE IMPORTE?', payload.details?.[0]?.imptotal);
      //console.log('🔍 [6] CLAVES PRIMERA PARTIDA:', Object.keys(payload.details?.[0] || {}));
      //console.log('🔍 [6] CLAVES PRIMERA PARTIDA:', Object.keys(payload.details?.[0] || {}));
      //console.log('🔍 [7] PARTIDA COMPLETA JSON:', JSON.stringify(payload.details?.[0], null, 2));  // ← NUEVA

      const r = await secureFetch(endpoint, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify(payload)
      });

      const j = await r.json();

      if (j?.success) {
        const record = j.data?.data || j.data;
        const continuarExito = () => {
          if (isCreate) {
            const createdId = record?.master_id || record?.id || record?.[idField] || j.id || j.insertId;
            onSuccess?.(createdId);
          } else {
            onSuccess?.(formData);
          }
          onClose?.();
        };

        // Mensaje de exito configurable por formulario (schema_form.confirm),
        // opt-in por operacion: exitoMensaje = CREATE, exitoMensajeUpdate = UPDATE.
        // Mismo patron que SqlexecMasterDetailLight: SqlexecMensaje y el flujo
        // normal de exito corre al pulsar Entendido. Sin config: toast y cierre de siempre.
        const exitoMensaje = isCreate ? schema?.confirm?.exitoMensaje : schema?.confirm?.exitoMensajeUpdate;
        if (exitoMensaje) {
          showMensajeModal('success', schema?.confirm?.exitoTitulo || 'Operación exitosa',
            resolvePlaceholders(exitoMensaje, { ...formData, ...(record && typeof record === 'object' ? record : {}) }),
            [], null, continuarExito);
        } else {
          toast.success(`${schema?.title || 'Registro'} ${isCreate ? 'creado' : 'actualizado'} correctamente`);
          continuarExito();
        }
        
      } else {
        if (r.status === 404) {
          toast.error('❌ El registro fue eliminado por otro usuario');
          onSuccess?.();
          setTimeout(() => onClose?.(), 100);
          return;
        }
        else if (r.status === 409) {
          if (j?.code === 'CONCURRENT_MODIFICATION') {
            setConflictData(j);
            setShowConflictModal(true);
            toast.warning('Conflicto detectado');
            return;
          }
          toast.error(j?.message || 'Ya existe un registro similar');
        } 
        else {
          toast.error(j?.error?.message || j?.message || 'Error al guardar');
        }
      }
    } catch (e) {
      console.error('❌ [FASE 1] Error:', e);

      logComponentError('SqlexecMasterDetail', 'submit_form', 
        `${componentForm} - ${mode} - ${e.message}`);

      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!idField) {
      console.error('❌ ERROR: idField no está definido');
      toast.error('Error interno: Campo ID no definido');
      return;
    }

    setSaving(true);
    try {
      const token = sessionStorage.getItem('mapasac_access_token');

      const r = await secureFetch(`/api/sqlexec/${componentForm}/delete`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ [idField]: initialData[idField] })
      });

      const j = await r.json();
      if (j?.success) {
        toast.success(`${schema?.title || 'Registro'} eliminado correctamente`);
        onSuccess?.();
        onClose?.();
      } else {
        toast.error(j?.error?.message || j?.message || 'Error al eliminar');
        onClose?.();
      }
    } catch (e) {
      logComponentError('SqlexecMasterDetail', 'delete_record', 
        `${componentForm} - ID: ${initialData[idField]} - ${e?.message || 'Unknown error'}`);
  
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyNavigation = (e, fieldName, fieldType) => {
    // 🔥 Si tiene error y presiona Tab o Enter — bloquear navegación
    if ((e.key === 'Tab' || e.key === 'Enter') && errors[fieldName]) {
      e.preventDefault();
      return;
    }

    const shouldSubmit = formNavigation.handleKeyDown(e, fieldName, fieldType);
    if (shouldSubmit && !isReadOnly && !isDelete) {
      const form = document.getElementById('sqlexec-form');
      if (form) {
        form.requestSubmit();
      }
    }
  };
  
  const ConflictModal = () => {
    const { modales_ui, botones_ui } = useSisConfig();

    if (!showConflictModal || !conflictData) return null;

    const headerStyles = modales_ui?.header || {};
    const btnBase = botones_ui || {};
    const btnPrimary = btnBase.primary || {};
    const btnSecondary = btnBase.secondary || {};

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]">
        <div className="bg-white border border-gray-200 rounded-lg shadow-2xl max-w-2xl w-full mx-4 p-6">
      
          <div className="flex items-center mb-4" style={{ gap: headerStyles.iconGap || '0.75rem' }}>
            <div 
              className="rounded-lg bg-orange-100"
              style={{
                padding: headerStyles.iconPadding || '0.5rem',
                borderRadius: headerStyles.iconBorderRadius || '0.5rem'
              }}
            >
              <AlertTriangle 
                className="text-orange-600" 
                size={parseInt(headerStyles.iconSize) || 24} 
              />
            </div>
            <h3 
              className="font-semibold"
              style={{
                fontSize: headerStyles.fontSize || '1.25rem',
                fontWeight: headerStyles.fontWeight || '600',
                color: headerStyles.textColor || '#111827'
              }}
            >
              Conflicto de Concurrencia
            </h3>
          </div>

          <div className="mb-6">
            <p className="text-sm mb-2 text-gray-700">
              {conflictData.message}
            </p>
            <p className="text-xs text-gray-500">
              {conflictData.hint}
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm font-medium mb-2 text-yellow-800">
              📊 Información del conflicto:
            </p>
            <div className="text-xs space-y-1 text-gray-700">
              <p>
                <strong>Tu versión:</strong> {new Date(conflictData.original_updated_at).toLocaleString('es-MX')}
              </p>
              <p>
                <strong>Versión actual:</strong> {new Date(conflictData.current_updated_at).toLocaleString('es-MX')}
              </p>
            </div>
          </div>

          <div className="flex justify-end" style={{ gap: '0.75rem' }}>
            <button
              onClick={() => {
                setShowConflictModal(false);
                setConflictData(null);
                onClose?.();
                onSuccess?.();
              }}
              className="transition-colors"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                paddingTop: btnBase.padding?.y || '0.5rem',
                paddingBottom: btnBase.padding?.y || '0.5rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: btnSecondary.borderColor || '#d1d5db',
                backgroundColor: btnSecondary.backgroundColor || '#ffffff',
                color: btnSecondary.textColor || '#374151',
                transitionDuration: btnBase.transitionDuration || '150ms',
                display: btnBase.display || 'flex',
                alignItems: btnBase.alignItems || 'center',
                justifyContent: btnBase.justifyContent || 'center',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = btnSecondary.hoverBackgroundColor || '#f9fafb';
                e.target.style.color = btnSecondary.hoverTextColor || '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = btnSecondary.backgroundColor || '#ffffff';
                e.target.style.color = btnSecondary.textColor || '#374151';
              }}
            >
              Cancelar
            </button>

            <button
              onClick={handleReloadFreshData}
              className="transition-colors flex items-center"
              style={{
                height: btnBase.height || '32px',
                fontSize: btnBase.fontSize || '0.75rem',
                fontFamily: btnBase.fontFamily || 'inherit',
                fontWeight: btnBase.fontWeight || '500',
                paddingLeft: btnBase.padding?.x || '1rem',
                paddingRight: btnBase.padding?.x || '1rem',
                paddingTop: btnBase.padding?.y || '0.5rem',
                paddingBottom: btnBase.padding?.y || '0.5rem',
                borderRadius: btnBase.borderRadius || '6px',
                borderWidth: btnBase.borderWidth || '1px',
                borderColor: btnPrimary.borderColor || '#3b82f6',
                backgroundColor: btnPrimary.backgroundColor || '#3b82f6',
                color: btnPrimary.textColor || '#ffffff',
                transitionDuration: btnBase.transitionDuration || '150ms',
                display: btnBase.display || 'flex',
                alignItems: btnBase.alignItems || 'center',
                justifyContent: btnBase.justifyContent || 'center',
                gap: '0.5rem',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = btnPrimary.hoverBackgroundColor || '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = btnPrimary.backgroundColor || '#3b82f6';
              }}
            >
              <RefreshCw size={16} />
              <span>Recargar Datos Actuales</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader2 className="animate-spin text-blue-500" size={48} />
        </div>
      </div>
    );
  }

  if (!schema && !showMensaje && mode !== 'delete') return null;

  const ICON_MAP = { 'check-circle': CheckCircle, 'alert-triangle': AlertTriangle, 'mail': Mail, 'save': Save, 'trash': Trash2, 'edit': Edit, 'eye': Eye };
  const HeaderIcon      = confirmHeader ? (ICON_MAP[confirmHeader.icon] || Save) : (isDelete ? Trash2 : isUpdate ? Edit : isReadOnly ? Eye : Save);
  const HeaderBg        = confirmHeader ? '' : (isDelete ? 'bg-red-100' : isUpdate ? 'bg-orange-100' : isReadOnly ? 'bg-green-100' : 'bg-blue-100');
  const HeaderIconColor = confirmHeader ? '' : (isDelete ? 'text-red-600' : isUpdate ? 'text-orange-600' : isReadOnly ? 'text-green-600' : 'text-blue-600');
  const HeaderIconStyle = confirmHeader ? { color: confirmHeader.iconColor } : {};
  const HeaderBgStyle   = confirmHeader ? { backgroundColor: confirmHeader.iconBg } : {};

  const gridConfig = formulario_ui?.grid || { columns: 2, gap: '1rem' };

  const getColSpan = (gridSpan) => {
    if (!gridSpan || gridSpan === 'full') return gridConfig.columns;
    
    const spanMap = {
      'half': Math.floor(gridConfig.columns / 2),
      '1/2':  Math.floor(gridConfig.columns / 2),
      '1/3':  Math.floor(gridConfig.columns / 3),
      '2/3':  Math.floor(gridConfig.columns * 2 / 3),
      '1/4':  Math.floor(gridConfig.columns / 4),
      '3/4':  Math.floor(gridConfig.columns * 3 / 4)
    };
    
    return spanMap[gridSpan] || 1;
  };

  const hasInvalidNumbers = () => {
    return schema.fields?.some(field => {

      if (field.type !== 'number' || !field.calculated) return false;
      
      const value = parseFloat(formData[field.name]);
      if (isNaN(value)) return false;
      
      if (field.min !== undefined && value < field.min) return true;
      
      return false;
    });
  };

  // 🔥 Obtener regla disableIfHasDetails — universal para select y search-modal
  const getDisableByDetails = (fieldName) => {
    const disableRules = detailSchema?.disableIfHasDetails || [];
    const disableRule = disableRules.find(r => r.field === fieldName);
    return {
      disableRule,
      isDisabledByDetails: !!(disableRule && detailData.length > 0)
    };
  };

  const handleComponentOnlyBlur = (rowIndex, col, numValue, navigate = false) => {
    const newData = [...detailData];
    const finalVal = (isNaN(numValue) || numValue < 0 || numValue > parseFloat(newData[rowIndex][col.maxField])) ? 0 : numValue;
    
    //newData[rowIndex][col.name] = finalVal;
    //setDetailData([...newData]);

    updateDetailCellValue(rowIndex, col.name, finalVal);
    if (!navigate) return;
    const nextRow = rowIndex + 1 < detailData.length ? rowIndex + 1 : 0;
    setTimeout(() => {
        const nextInput = document.querySelector(`input[data-row="${nextRow}"][data-field="${col.name}"]`);
        if (nextInput) { nextInput.focus(); nextInput.select(); }
    }, 50);
  };

  return (
    <>
      <div 
        className={`fixed inset-0 flex items-center justify-center z-50 ${moduleUIConfig.fullscreen ? 'p-1' : 'p-4'}`}
        style={{
          backgroundColor: tabla_ui?.modals?.overlay?.backgroundColor || 'rgba(0, 0, 0, 0.6)',
          backdropFilter: tabla_ui?.modals?.overlay?.backdropFilter || 'blur(4px)'
        }}
      >
        <div
          className="w-full"
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: modales_ui?.container?.backgroundColor || '#ffffff',
            borderRadius: modales_ui?.container?.borderRadius || '12px',
            boxShadow: modales_ui?.container?.boxShadow || '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: modales_ui?.container?.border || '1px solid rgba(0, 0, 0, 0.05)',
            maxWidth: moduleUIConfig.fullscreen 
              ? compensarEscalaVh(moduleUIConfig.containerMaxWidth || '98vw')
              : (schema?.modalWidth || (modales_ui?.container?.maxWidth === '4xl' ? '56rem' : compensarEscalaVh(modales_ui?.container?.maxWidth) || '56rem')),
                        maxHeight: moduleUIConfig.fullscreen
              ? compensarEscalaVh(moduleUIConfig.containerMaxHeight || '98vh')
              : compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh'),
            overflow: modales_ui?.container?.overflow || 'hidden'
          }}
        >
          <div 
            className="flex items-center justify-between"
            style={{
              paddingLeft: modales_ui?.header?.paddingX || '2rem',
              paddingRight: modales_ui?.header?.paddingX || '2rem',
              paddingTop: modales_ui?.header?.paddingY || '1.5rem',
              paddingBottom: modales_ui?.header?.paddingY || '1.5rem',
              borderBottom: modales_ui?.header?.borderBottom || '1px solid #e5e7eb',
              backgroundColor: modales_ui?.header?.backgroundColor || '#fafafa'
            }}
          >
            <div 
              className="flex items-center"
              style={{ gap: modales_ui?.header?.iconGap || '1rem' }}
            >
              <div 
                className={`${HeaderBg}`}
                style={{
                  padding: modales_ui?.header?.iconPadding || '0.625rem',
                  borderRadius: modales_ui?.header?.iconBorderRadius || '10px',
                  ...HeaderBgStyle
                }}
              >
                <HeaderIcon 
                  className={HeaderIconColor}
                  style={HeaderIconStyle}
                  size={parseInt(modales_ui?.header?.iconSize) || 28} 
                />
              </div>
              <div>
              <h2 
                className="font-semibold"
                style={{
                  fontSize: modales_ui?.header?.fontSize || '1.5rem',
                  fontWeight: modales_ui?.header?.fontWeight || '600',
                  color: modales_ui?.header?.textColor || '#111827',
                  letterSpacing: modales_ui?.header?.letterSpacing || '-0.025em',
                  lineHeight: modales_ui?.header?.lineHeight || '1.2'
                }}
              >
                {confirmHeader?.titulo || initialData?._title_override ||
                  (componentForm === 'Sys_Envio_Email' && saldoCorreos === -1
                    ? `${schema?.title} (Correos: sin configurar)`
                    : componentForm === 'Sys_Envio_Email' && saldoCorreos !== null && saldoCorreos >= 0
                      ? `${schema?.title} (Correos: ${saldoCorreos})`
                      : `${isDelete ? 'Eliminar' : isUpdate ? 'Editar' : isReadOnly ? 'Consultar' : 'Crear'} ${schema?.title}`)}
              </h2>
                <SqlexecIdTag idsqlexec={idsqlexec} componentForm={componentForm} config={panel_ui?.identificador} />
              </div>
            </div>

            <button 
              onClick={handleCancel} 
              className="transition-colors"
              style={{
                padding: modales_ui?.closeButton?.padding || '0.5rem',
                borderRadius: modales_ui?.closeButton?.borderRadius || '8px',
                color: modales_ui?.closeButton?.color || '#6b7280',
                transition: modales_ui?.closeButton?.transition || 'all 150ms ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = modales_ui?.closeButton?.hoverBackgroundColor || '#f3f4f6';
                e.target.style.color = modales_ui?.closeButton?.hoverColor || '#111827';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = modales_ui?.closeButton?.color || '#6b7280';
              }}
            >
              <X size={parseInt(modales_ui?.closeButton?.size) || 20} />
            </button>
          </div>

          {isDelete ? (
            <>
              <div 
                className="text-center"
                style={{
                  padding: modales_ui?.content?.padding || '2rem',
                  paddingTop: modales_ui?.content?.paddingTop || '1.5rem',
                  paddingBottom: modales_ui?.content?.paddingBottom || '2rem'
                }}
              >
                <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle size={40} className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-gray-900">¿Estás seguro?</h3>
                <p className="text-base mb-2 text-gray-900">
                  Id Registro # {initialData[idField]} Valida.
                </p>
                <p className="text-sm text-gray-600">
                  Esta acción no se puede deshacer.
                </p>
              </div>

              <div 
                className="flex"
                style={{
                  paddingLeft: modales_ui?.footer?.paddingX || '2rem',
                  paddingRight: modales_ui?.footer?.paddingX || '2rem',
                  paddingTop: modales_ui?.footer?.paddingY || '1.25rem',
                  paddingBottom: modales_ui?.footer?.paddingBottom || '1.25rem',
                  borderTop: modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
                  backgroundColor: modales_ui?.footer?.backgroundColor || '#fafafa',
                  gap: modales_ui?.footer?.gap || '0.75rem',
                  justifyContent: 'center'
                }}
              >
                <button 
                  onClick={handleCancel}
                  disabled={saving} 
                  className="transition-colors"
                  style={{
                    height: botones_ui?.height || '32px',
                    fontSize: botones_ui?.fontSize || '0.75rem',
                    fontFamily: botones_ui?.fontFamily || 'inherit',
                    fontWeight: botones_ui?.fontWeight || '500',
                    paddingLeft: '1.5rem',
                    paddingRight: '1.5rem',
                    paddingTop: botones_ui?.padding?.y || '0.5rem',
                    paddingBottom: botones_ui?.padding?.y || '0.5rem',
                    borderRadius: botones_ui?.borderRadius || '6px',
                    borderWidth: botones_ui?.borderWidth || '1px',
                    borderColor: botones_ui?.secondary?.borderColor || '#d1d5db',
                    backgroundColor: botones_ui?.secondary?.backgroundColor || '#ffffff',
                    color: botones_ui?.secondary?.textColor || '#374151',
                    transitionDuration: botones_ui?.transitionDuration || '150ms',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? '0.5' : '1',
                    display: botones_ui?.display || 'flex',
                    alignItems: botones_ui?.alignItems || 'center',
                    justifyContent: botones_ui?.justifyContent || 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = botones_ui?.secondary?.hoverBackgroundColor || '#f9fafb';
                      e.target.style.color = botones_ui?.secondary?.hoverTextColor || '#111827';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = botones_ui?.secondary?.backgroundColor || '#ffffff';
                      e.target.style.color = botones_ui?.secondary?.textColor || '#374151';
                    }
                  }}
                >
                  Cancelar
                </button>

                <button 
                  onClick={handleDelete} 
                  disabled={saving} 
                  className="transition-colors"
                  style={{
                    height: botones_ui?.height || '32px',
                    fontSize: botones_ui?.fontSize || '0.75rem',
                    fontFamily: botones_ui?.fontFamily || 'inherit',
                    fontWeight: botones_ui?.fontWeight || '500',
                    paddingLeft: '1.5rem',
                    paddingRight: '1.5rem',
                    paddingTop: botones_ui?.padding?.y || '0.5rem',
                    paddingBottom: botones_ui?.padding?.y || '0.5rem',
                    borderRadius: botones_ui?.borderRadius || '6px',
                    borderWidth: botones_ui?.borderWidth || '1px',
                    borderColor: saving 
                      ? (botones_ui?.danger?.disabledBorderColor || '#9ca3af')
                      : (botones_ui?.danger?.borderColor || '#dc2626'),
                    backgroundColor: saving 
                      ? (botones_ui?.danger?.disabledBackgroundColor || '#9ca3af')
                      : (botones_ui?.danger?.backgroundColor || '#dc2626'),
                    color: saving 
                      ? (botones_ui?.danger?.disabledTextColor || '#ffffff')
                      : (botones_ui?.danger?.textColor || '#ffffff'),
                    transitionDuration: botones_ui?.transitionDuration || '150ms',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    display: botones_ui?.display || 'flex',
                    alignItems: botones_ui?.alignItems || 'center',
                    justifyContent: botones_ui?.justifyContent || 'center'
                  }}
                  onMouseEnter={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = botones_ui?.danger?.hoverBackgroundColor || '#b91c1c';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!saving) {
                      e.target.style.backgroundColor = botones_ui?.danger?.backgroundColor || '#dc2626';
                    }
                  }}
                >
                  {saving ? <Loader2 className="animate-spin" size={18} /> : 'Sí, eliminar'}
                </button>
              </div>
            </>
          ) : (
            <>
              <form
                ref={formRef}
                id="sqlexec-form"
                onSubmit={handleSubmit} 
                className="overflow-y-auto"
                style={{
                  overflowX: 'hidden',
                  padding: moduleUIConfig.fullscreen && moduleUIConfig.reducePadding
                    ? (moduleUIConfig.contentPadding || '1rem')
                    : (modales_ui?.content?.padding || '2rem'),
                  paddingTop: moduleUIConfig.fullscreen && moduleUIConfig.reducePadding
                    ? (moduleUIConfig.contentPadding || '1rem')
                    : (modales_ui?.content?.paddingTop || '1.5rem'),
                  paddingBottom: moduleUIConfig.fullscreen && moduleUIConfig.reducePadding
                    ? (moduleUIConfig.contentPadding || '1rem')
                    : (modales_ui?.content?.paddingBottom || '1.5rem'),
                  maxHeight: moduleUIConfig.fullscreen
                    ? `calc(${compensarEscalaVh(moduleUIConfig.containerMaxHeight || '98vh')} - 220px)`
                    : `calc(${compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh')} - 220px)`,
                  // collapsibleHeader.fillDetailHeight: solo colapsado, la grilla llena hasta el pie
                  ...(fillDetailCollapsed && {
                    height: moduleUIConfig.fullscreen
                      ? `calc(${compensarEscalaVh(moduleUIConfig.containerMaxHeight || '98vh')} - 220px)`
                      : `calc(${compensarEscalaVh(modales_ui?.container?.maxHeight || '92vh')} - 220px)`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'hidden'
                  })
                }}
              >
                {isCollapsibleHeader && (() => {
                  const labelStyles = formulario_ui?.labels || {};
                  const bloqueado = !headerCollapsed && !canCollapseHeader;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, marginBottom: labelStyles.marginBottom || '0.5rem' }}>
                      <label style={{
                        fontSize:   labelStyles.fontSize || '0.875rem',
                        fontWeight: labelStyles.fontWeight || '600',
                        color:      labelStyles.textColor || '#374151'
                      }}>
                        {collapsibleHeaderCfg?.label || ''}
                      </label>
                      <button
                        type="button"
                        onClick={() => !bloqueado && setHeaderCollapsed(c => !c)}
                        disabled={bloqueado}
                        style={{
                          background: 'none', border: 'none',
                          cursor: bloqueado ? 'not-allowed' : 'pointer',
                          opacity: bloqueado ? (formulario_ui?.inputs?.disabledOpacity || 0.4) : 1,
                          color: labelStyles.textColor || '#374151',
                          display: 'flex', alignItems: 'center'
                        }}
                      >
                        {headerIsCollapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
                      </button>
                    </div>
                  );
                })()}

                {!headerIsCollapsed && (() => {
                  const inputStyles = formulario_ui?.inputs || {};
                  const selectStyles = formulario_ui?.selects || {};
                  const labelStyles = formulario_ui?.labels || {};
                  const checkboxStyles = formulario_ui?.checkboxes || {};
                  const errorStyles = formulario_ui?.errorMessages || {};
                  const btnBase = botones_ui || {};

                  return (
                    <div 
                      className="grid"
                      style={{
                        gridTemplateColumns: `repeat(${gridConfig.columns}, 1fr)`,
                        gap: gridConfig.gap
                      }}
                    >
                      {(schema.fields || []).map((f) => {
                        if (f.type === 'hidden') return null;

                        if (f.conditionalVisible) {
                          const { dependsOn, rules } = f.conditionalVisible;
                          const depVal = formData[dependsOn];
                          const rule = rules?.find(r => String(r.value) === String(depVal));
                          if (rule && rule.visible === false) return null;
                        }

                        let isRequired = !!f.required;
                        if (f.conditionalRequired) {
                          const { dependsOn, rules } = f.conditionalRequired;
                          const depVal = formData[dependsOn];
                          const rule = rules?.find(r => String(r.value) === String(depVal));
                          if (rule) isRequired = !!rule.required;
                        }

                        const showError = touched[f.name] && errors[f.name];
                        const isValidatingUnique = uniqueValidation.validating[f.name];

                        if (f.type === 'checkbox') {
                          return (
                            <div 
                              key={f.name}
                              style={{
                                gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                              }}
                            >
                              <label 
                                className="block"
                                style={{
                                  fontSize: labelStyles.fontSize || '0.875rem',
                                  fontWeight: labelStyles.fontWeight || '500',
                                  color: labelStyles.textColor || '#374151',
                                  marginBottom: labelStyles.marginBottom || '0.5rem'
                                }}
                              >
                                {f.label}
                              </label>
                              
                              <input
                                ref={el => formNavigation.registerRef(f.name, el)}
                                type="checkbox"
                                checked={!!formData[f.name]}
                                onChange={e => handleChange(f.name, e.target.checked, f, e)}
                                onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                                disabled={isReadOnly}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: isReadOnly ? 'not-allowed' : 'pointer'
                                }}
                              />
                            </div>
                          );
                        }

                        if (f.type === 'select') {
                          const { disableRule, isDisabledByDetails } = getDisableByDetails(f.name);
                          return (
                            <div 
                              key={f.name}
                              style={{
                                gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                              }}
                              onMouseEnter={(e) => {
                                if (isDisabledByDetails && disableRule?.tooltip) {
                                  showTooltip(disableRule.message, e.currentTarget);
                                  setTimeout(() => showTooltip(null, null), disableRule.tooltipTimeout || 3000);
                                }
                              }}
                            >
                              <label 
                                className="block"
                                style={{
                                  fontSize: labelStyles.fontSize || '0.875rem',
                                  fontWeight: labelStyles.fontWeight || '500',
                                  color: labelStyles.textColor || '#374151',
                                  marginBottom: labelStyles.marginBottom || '0.5rem'
                                }}
                              >
                                {f.dynamicLabel ? (formData[f.dynamicLabel] || f.label) : f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              <div className="relative">
                                <select
                                  ref={el => formNavigation.registerRef(f.name, el)}
                                  value={formData[f.name] || ''}


                                  ////onChange={e => handleChange(f.name, e.target.value, f)}
                                  onChange={e => {
                                    handleChange(f.name, e.target.value, f);
                                    handleSelectReturnFields(f, e.target.value);
                                  }}


                                  onFocus={() => setFocusedField(f.name)}
                                  onBlur={() => setFocusedField(null)}
                                  onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                                  disabled={isReadOnly || loadingField[f.name] || isDisabledByDetails}
                                  className={`w-full pr-10 appearance-none ${showError ? 'border-red-500' : ''}`}
                                  style={{
                                    height: selectStyles.height || '32px',
                                    fontSize: selectStyles.fontSize || '0.75rem',
                                    fontFamily: selectStyles.fontFamily || 'inherit',
                                    paddingLeft: selectStyles.padding?.x || '0.75rem',
                                    paddingRight: selectStyles.padding?.x || '0.75rem',
                                    paddingTop: selectStyles.padding?.y || '0.5rem',
                                    paddingBottom: selectStyles.padding?.y || '0.5rem',
                                    borderRadius: selectStyles.borderRadius || '6px',
                                    borderWidth: selectStyles.borderWidth || '1px',
                                    borderColor: showError ? '#ef4444' : focusedField === f.name ? (selectStyles.focusBorderColor || '#3b82f6') : (selectStyles.borderColor || '#d1d5db'),
                                    outline: 'none',
                                    boxShadow: focusedField === f.name ? `0 0 0 ${selectStyles.focusRingWidth || '2px'} ${selectStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                                    backgroundColor: (isReadOnly || isDisabledByDetails) 
                                      ? (selectStyles.disabledBackgroundColor || '#f3f4f6')
                                      : (selectStyles.backgroundColor || '#ffffff'),
                                    color: (formData[f.name] === '' || formData[f.name] === null || formData[f.name] === undefined) 
                                      ? (selectStyles.placeholderColor || '#9ca3af')
                                      : (selectStyles.textColor || '#111827'),
                                    opacity: (isReadOnly || isDisabledByDetails) ? (selectStyles.disabledOpacity || '0.5') : '1',
                                    display: btnBase.display || 'flex',
                                    alignItems: btnBase.alignItems || 'center',
                                    justifyContent: btnBase.justifyContent || 'center'
                                  }}
                                >
                                  <option value="">{loadingField[f.name] ? 'Cargando...' : 'Seleccionar...'}</option>
                                  {selectOptions[f.name]?.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>

                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                  {loadingField[f.name] ? (
                                    <Loader2 size={16} className="text-blue-500 animate-spin" />
                                  ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400"
                                      fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              {showError && (
                                <p 
                                  style={{
                                    fontSize: errorStyles.fontSize || '0.75rem',
                                    color: errorStyles.textColor || '#dc2626',
                                    marginTop: errorStyles.marginTop || '0.25rem'
                                  }}
                                >
                                  {errors[f.name]?.message || errors[f.name]}
                                </p>
                              )}
                            </div>
                          );
                        }

                        if (f.type === 'search-modal') {
                          const displayFieldName = `${f.name}_display`;
                          const initialDisplay = formData[displayFieldName] || null;
                          const { disableRule, isDisabledByDetails } = getDisableByDetails(f.name);
                          return (
                            <div
                              key={f.name}
                              style={{ gridColumn: `span ${getColSpan(f.gridSpan || 'full')}` }}
                              onMouseEnter={(e) => {
                                if (isDisabledByDetails && disableRule.tooltip) {
                                  showTooltip(disableRule.message, e.currentTarget);
                                  setTimeout(() => showTooltip(null, null), disableRule.tooltipTimeout || 3000);
                                }
                              }}
                            >
                              <SearchModalField
                                field={{
                                  ...f,
                                  initialDisplayValue: initialDisplay
                                }}
                                value={formData[f.name] || null}
                                formData={formData}
                                onChange={(value) => handleChange(f.name, value, f)}
                                onMultiFieldChange={(fields) => {
                                  //console.log('🔍 SearchModal returnFields:', JSON.stringify(fields, null, 2));
                                  setPreservedValues(prev => ({ ...prev, ...fields }));
                                  setFormData(prev => ({ ...prev, ...fields }));
                                }}
                                disabled={isReadOnly || isDisabledByDetails}
                                error={errors[f.name]}
                                touched={touched[f.name]}
                                inputStyles={inputStyles}
                                labelStyles={labelStyles}
                                errorStyles={errorStyles}
                              />
                            </div>
                          );
                        }

                        if (f.type === 'attachments') {
                          return (
                            <div
                              key={f.name}
                              style={{
                                gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                              }}
                            >
                              <AttachmentsField
                                value={formData[f.name] || []}
                                onChange={(val) => handleChange(f.name, val, f)}
                                adjuntosConfig={emailConfig?.adjuntos || {}}
                                disabled={isReadOnly}
                                labelStyles={labelStyles}
                                inputStyles={inputStyles}
                              />
                            </div>
                          );
                        }

                        if (f.type === 'textarea') {
                          return (
                            <div 
                              key={f.name}
                              style={{
                                gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                              }}
                            >
                              <label 
                                className="block"
                                style={{
                                  fontSize: labelStyles.fontSize || '0.875rem',
                                  fontWeight: labelStyles.fontWeight || '500',
                                  color: labelStyles.textColor || '#374151',
                                  marginBottom: labelStyles.marginBottom || '0.5rem'
                                }}
                              >
                                {f.dynamicLabel ? (formData[f.dynamicLabel] || f.label) : f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              <textarea
                                ref={el => {
                                  formNavigation.registerRef(f.name, el);
                                  if (el) {
                                    el.style.height = 'auto';
                                    el.style.height = `${el.scrollHeight}px`;
                                  }
                                }}
                                value={formData[f.name] ?? ''}

                                onChange={e => {
                                  e.target.style.height = 'auto';
                                  e.target.style.height = `${e.target.scrollHeight}px`;
                                  handleChange(f.name, e.target.value, f, e);
                                }}
                                onFocus={e => {
                                  setFocusedField(f.name);
                                  e.target.style.height = 'auto';
                                  e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                onBlur={() => { setFocusedField(null); handleBlur(f.name, f); }}
                                placeholder={f.placeholder}
                                maxLength={f.maxLength}
                                disabled={isReadOnly || f.disabled}
                                className={`w-full ${showError ? 'border-red-500' : ''}`}
                                style={{
                                  fontSize: inputStyles.fontSize || '0.75rem',
                                  fontFamily: inputStyles.fontFamily || 'inherit',
                                  paddingLeft: inputStyles.padding?.x || '0.75rem',
                                  paddingRight: inputStyles.padding?.x || '0.75rem',
                                  paddingTop: inputStyles.padding?.y || '0.5rem',
                                  paddingBottom: inputStyles.padding?.y || '0.5rem',
                                  borderRadius: inputStyles.borderRadius || '6px',
                                  borderWidth: inputStyles.borderWidth || '1px',
                                  borderColor: showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (schema?.submitAction === 'confirm_only' ? 'transparent' : (inputStyles.borderColor || '#d1d5db')),
                                  outline: 'none',
                                  boxShadow: focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                                  backgroundColor: (isReadOnly || f.disabled) 
                                    ? (schema?.submitAction === 'confirm_only' ? 'transparent' : '#f3f4f6') 
                                    : (inputStyles.backgroundColor || '#ffffff'),
                                  color: inputStyles.textColor || '#111827',                                  
                                  opacity: isReadOnly ? (selectStyles.disabledOpacity || '0.5') : '1',
                                  minHeight: 'unset',
                                  maxHeight: '300px',
                                  resize: 'none',
                                  overflow: 'auto'
                                }}

                              />
                              {showError && (
                                <p 
                                  style={{
                                    fontSize: errorStyles.fontSize || '0.75rem',
                                    color: errorStyles.textColor || '#dc2626',
                                    marginTop: errorStyles.marginTop || '0.25rem'
                                  }}
                                >
                                  {errors[f.name]}
                                </p>
                              )}
                            </div>
                          );
                        }

                        return (
                          <div 
                            key={f.name} 
                            className="relative"
                            style={{
                              gridColumn: `span ${getColSpan(f.gridSpan || 'full')}`
                            }}
                          >
                            <label 
                              className="block"
                              style={{
                                fontSize: labelStyles.fontSize || '0.875rem',
                                fontWeight: labelStyles.fontWeight || '500',
                                color: labelStyles.textColor || '#374151',
                                marginBottom: labelStyles.marginBottom || '0.5rem'
                              }}
                            >
                              {f.dynamicLabel ? (formData[f.dynamicLabel] || f.label) : f.label}{!isReadOnly && isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <input
                              ref={el => formNavigation.registerRef(f.name, el)}
                              type={f.type}
                              value={formData[f.name] ?? ''}
                              onChange={e => handleChange(f.name, e.target.value, f, e)}
                              onFocus={() => setFocusedField(f.name)}
                              onBlur={() => { setFocusedField(null); handleBlur(f.name, f); }}
                              onKeyDown={e => handleKeyNavigation(e, f.name, f.type)}
                              placeholder={f.placeholder}
                              maxLength={f.maxLength}
                              pattern={f.pattern}
                              min={f.min}
                              max={f.max}
                              step={f.step}
                              disabled={isReadOnly || (() => {
                                if (f.conditionalDisabled) {
                                  const { dependsOn, rules, defaultDisabled } = f.conditionalDisabled;
                                  const depValue = formData[dependsOn];                                  
                                  const rule = rules?.find(r => parseFloat(r.value) === parseFloat(depValue));                                  
                                  if (rule) {
                                    return rule.disabled;
                                  }                                  
                                  return defaultDisabled ?? f.disabled;
                                }                                
                                return f.disabled;
                              })()}
                              className={`w-full ${showError ? 'border-red-500' : ''}`}
                              style={{
                                height: inputStyles.height || '32px',
                                fontSize: inputStyles.fontSize || '0.75rem',
                                fontFamily: inputStyles.fontFamily || 'inherit',
                                paddingLeft: inputStyles.padding?.x || '0.75rem',
                                paddingRight: inputStyles.padding?.x || '0.75rem',
                                paddingTop: inputStyles.padding?.y || '0.5rem',
                                paddingBottom: inputStyles.padding?.y || '0.5rem',
                                borderRadius: inputStyles.borderRadius || '6px',
                                borderWidth: inputStyles.borderWidth || '1px',
                                borderColor: showError ? '#ef4444' : focusedField === f.name ? (inputStyles.focusBorderColor || '#3b82f6') : (schema?.submitAction === 'confirm_only' ? 'transparent' : (inputStyles.borderColor || '#d1d5db')),
                                outline: 'none',
                                boxShadow: focusedField === f.name ? `0 0 0 ${inputStyles.focusRingWidth || '2px'} ${inputStyles.focusBorderColor || '#3b82f6'}40` : 'none',
                                backgroundColor: (isReadOnly || f.disabled) 
                                  ? (schema?.submitAction === 'confirm_only' ? 'transparent' : '#f3f4f6') 
                                  : (inputStyles.backgroundColor || '#ffffff'),
                                color: inputStyles.textColor || '#111827',
                                opacity: isReadOnly ? (selectStyles.disabledOpacity || '0.5') : '1'
                              }}
                            />

                            {isValidatingUnique && (
                              <div className="absolute right-3 top-9 pointer-events-none">
                                <Loader2 size={16} className="animate-spin text-blue-500" />
                              </div>
                            )}

                            {showError && (
                              <p 
                                style={{
                                  fontSize: errorStyles.fontSize || '0.75rem',
                                  color: errorStyles.textColor || '#dc2626',
                                  marginTop: errorStyles.marginTop || '0.25rem'
                                }}
                              >
                                {errors[f.name]}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {detailSchema && !isDelete && (
                  <div 
                    className="mt-1 pt-2"
                    style={{
                      marginLeft: `calc(-1 * (${modales_ui?.content?.padding || '2rem'}))`,
                      marginRight: `calc(-1 * (${modales_ui?.content?.padding || '2rem'}))`,
                      paddingLeft: modales_ui?.content?.padding || '2rem',
                      paddingRight: modales_ui?.content?.padding || '2rem',
                      ...(fillDetailCollapsed && { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' })
                    }}
                  >
                    {detailSchema.showLabel !== false && detailSchema.label && (
                      <label
                        className="block"
                        style={{
                          fontSize: detailSchema.labelStyles?.fontSize || '0.875rem',
                          fontWeight: detailSchema.labelStyles?.fontWeight || '600',
                          color: detailSchema.labelStyles?.color || '#374151',
                          marginBottom: detailSchema.labelStyles?.marginBottom || '0.5rem'
                        }}
                      >
                        {detailSchema.label}
                      </label>
                    )}
                    <div 
                      className="overflow-hidden"
                      style={{
                        border: tabla_ui?.table?.border || '1px solid #d1d5db',
                        ...(fillDetailCollapsed && { flex: 1, minHeight: 0, overflowY: 'auto' })
                      }}
                    >
                      <table 
                        className="w-full text-sm"
                        style={{
                          border: tabla_ui?.table?.border || '1px solid #d1d5db',
                          borderCollapse: tabla_ui?.table?.borderCollapse || 'collapse'
                        }}
                      >
                        <thead className="bg-gray-100" style={fillDetailCollapsed ? { position: 'sticky', top: 0, zIndex: 1 } : undefined}>
                          <tr>
                            {detailSchema.columns
                                ?.filter(col => {
                                  if (col.componentOnly && !isComponentMode && !(col.viewOnly && isReadOnly)) return false;
                                  return true;
                                })
                                ?.map(col => (
                              <th 
                                key={col.name}
                                className="whitespace-nowrap"
                                style={{ 
                                  width: col.width,
                                  textAlign: headerConfig.textAlign || col.align || 'left',
                                  paddingLeft: headerConfig.padding?.x || '0.50rem',
                                  paddingRight: headerConfig.padding?.x || '0.50rem',
                                  paddingTop: headerConfig.padding?.y || '0.50rem',
                                  paddingBottom: headerConfig.padding?.y || '0.50rem',
                                  fontSize: headerConfig.fontSize || '0.75rem',
                                  fontFamily: headerConfig.fontFamily || 'inherit',
                                  fontWeight: headerConfig.fontWeight || '600',
                                  textTransform: headerConfig.textTransform || 'uppercase',
                                  color: headerConfig.color || '#374151',
                                  height: headerConfig.height || 'auto',
                                  backgroundColor: headerConfig.backgroundColor || '#f3f4f6',
                                  borderBottom: headerConfig.borderBottom 
                                    ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                                    : '2px solid #e5e7eb',
                                  borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                                  borderTop: headerConfig.borderTop || '1px solid #d1d5db'
                                }}
                              >
                                {col.type === 'checkbox' && col.fillOnCheck
                                  ? <input
                                      type="checkbox"
                                      onChange={(e) => {
                                        const newData = [...detailData];
                                        newData.forEach((row, idx) => {
                                          const val = e.target.checked ? parseFloat(row[col.fillOnCheck.source]) || 0 : 0;
                                          newData[idx][col.name] = e.target.checked;
                                          updateDetailCellValue(idx, col.fillOnCheck.target, val);
                                        });
                                      }}
                                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                    />
                                  : col.label
                                }
                                {col.required && <span className="text-red-500 ml-1">*</span>}
                              </th>
                            ))}
                            {/* 🔥 CAMBIAR CONDICIÓN: detailSchema.allowDelete → !isReadOnly */}
                            {!isReadOnly && (
                              <th 
                                className="whitespace-nowrap"
                                style={{
                                  width: '64px',
                                  textAlign: tabla_ui?.actionsColumn?.header?.textAlign || 'center',
                                  paddingLeft: tabla_ui?.actionsColumn?.header?.padding?.x || '0.75rem',
                                  paddingRight: tabla_ui?.actionsColumn?.header?.padding?.x || '0.75rem',
                                  paddingTop: tabla_ui?.actionsColumn?.header?.padding?.y || '0.50rem',
                                  paddingBottom: tabla_ui?.actionsColumn?.header?.padding?.y || '0.50rem',
                                  fontSize: tabla_ui?.actionsColumn?.header?.fontSize || '0.75rem',
                                  fontFamily: tabla_ui?.actionsColumn?.header?.fontFamily || 'inherit',
                                  fontWeight: tabla_ui?.actionsColumn?.header?.fontWeight || '600',
                                  textTransform: tabla_ui?.actionsColumn?.header?.textTransform || 'uppercase',
                                  color: tabla_ui?.actionsColumn?.header?.color || '#374151',
                                  height: headerConfig.height || 'auto',
                                  backgroundColor: headerConfig.backgroundColor || '#f3f4f6',
                                  borderBottom: headerConfig.borderBottom 
                                    ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                                    : '2px solid #e5e7eb',
                                  borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                                  borderTop: headerConfig.borderTop || '1px solid #d1d5db'
                                }}
                              >
                                {tabla_ui?.actionsColumn?.label || 'Acciones'}
                              </th>
                            )}
                          </tr>
                        </thead>
                        
                        <tbody>
                          {detailData.length === 0 ? (
                            <tr>
                              <td 
                                colSpan={detailSchema.columns?.filter(col => isComponentMode ? true : !col.componentOnly)?.length + (!isReadOnly ? 1 : 0)}
                                className="text-center py-12"
                                style={{
                                  borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                }}
                              >
                                <div className="flex flex-col items-center justify-center gap-3">
                                  <div 
                                    className="rounded-full flex items-center justify-center"
                                    style={{
                                      width: '64px',
                                      height: '64px',
                                      backgroundColor: '#f3f4f6'
                                    }}
                                  >
                                    <ShoppingCart size={32} className="text-gray-400" />
                                  </div>
                                  <div className="flex flex-col gap-1">
                                    <p 
                                      className="font-medium"
                                      style={{
                                        fontSize: '0.875rem',
                                        color: '#6b7280'
                                      }}
                                    >
                                      No hay partidas en la cotización
                                    </p>
                                    <p 
                                      style={{
                                        fontSize: '0.75rem',
                                        color: '#6b7280'
                                      }}
                                    >
                                      Presiona "+ Agregar Partida" para comenzar
                                    </p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            detailData.map((row, rowIndex) => {
                              const rowBackground = rowIndex % 2 === 0
                                ? (rowsConfig.odd || '#ffffff')
                                : (rowsConfig.even || '#f9fafb');
                              return (
                              <tr 
                                key={row._id}
                                style={{ backgroundColor: rowBackground }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = rowsConfig.hoverColor || '#f0f7ff'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = rowBackground}
                                onClick={() => {
                                    if (!row.partida_completa) {
                                        const input = document.querySelector(`input[data-row="${rowIndex}"][data-field="cantidad"]`);
                                        if (input) { input.focus(); input.select(); }
                                    }
                                }}
                              >
                                {detailSchema.columns
                                    ?.filter(col => {
                                      if (col.componentOnly && !isComponentMode && !(col.viewOnly && isReadOnly)) return false;
                                      return true;
                                    })
                                    ?.map(col => (
                                  <td 
                                    key={col.name}
                                    className=""
                                    style={{ 
                                      textAlign: col.align || 'left',
                                      paddingLeft: '0.5rem',
                                      paddingRight: '0.5rem',
                                      paddingTop: '0.125rem',
                                      paddingBottom: '0.125rem',
                                      fontSize: rowsConfig.fontSize || '0.75rem',
                                      fontFamily: rowsConfig.fontFamily || 'inherit',
                                      color: rowsConfig.textColor || '#374151',
                                      maxWidth: columnsConfig.maxWidth || '180px',
                                      borderRight: rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}
                                  >
                                    {(() => {

                                      // 🔥 DESCRIPCIÓN LIBRE — textarea inline
                                      if (col.name === 'producto' && row._editandoDescLibre === true) {
                                        return (
                                          <textarea
                                            autoFocus
                                            rows={3}
                                            maxLength={1000}
                                            value={row[col.name] || ''}
                                            onChange={(e) => {
                                              e.target.style.height = 'auto';
                                              e.target.style.height = `${e.target.scrollHeight}px`;
                                              const newData = [...detailData];
                                              const valor = conservarCursor(e.target, (texto) => {
                                                let v = texto.toUpperCase();
                                                if (col.sanitize) v = sanitizeOnType(v, col.sanitize);
                                                return v;
                                              });
                                              newData[rowIndex][col.name] = valor;
                                              setDetailData(newData);
                                            }}

                                            onFocus={(e) => {
                                              e.target.style.height = 'auto';
                                              e.target.style.height = `${e.target.scrollHeight}px`;
                                              if (col.tooltipEdit) {
                                                showTooltip(col.tooltipEdit, e.target);
                                                // 🔥 Ocultar automáticamente después de 2 segundos
                                                setTimeout(() => showTooltip(null, null), 5000);
                                              }
                                            }}

                                            onBlur={() => {
                                              showTooltip(null, null);
                                              const newData = [...detailData];
                                              let descripcion = newData[rowIndex][col.name] || '';
                                              if (col.sanitize) {
                                                  descripcion = sanitizeOnBlur(descripcion, col.sanitize);
                                              }
                                              newData[rowIndex][col.name] = descripcion.toUpperCase();
                                              newData[rowIndex]._editandoDescLibre = false;
                                              setDetailData(newData);
                                              setIsDirty(true);
                                              setTimeout(() => {
                                                const buttons = document.querySelectorAll('button[type="button"]');
                                                const addButton = Array.from(buttons).find(btn => 
                                                  btn.textContent.includes('+ Agregar Partida')
                                                );
                                                if (addButton) addButton.focus();
                                              }, 50);
                                            }}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Escape') {
                                                const newData = [...detailData];
                                                newData[rowIndex]._editandoDescLibre = false;
                                                setDetailData(newData);
                                              }
                                            }}
                                            className="w-full px-2 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:border-blue-600"
                                            style={{ 
                                              minHeight: '60px',
                                              maxHeight: '200px',
                                              resize: 'none',      // 🔥 sin resize manual
                                              overflow: 'auto',
                                              minWidth: '300px'
                                            }}
                                          />
                                        );
                                      }

                                      // 🔥 NUEVO: Detectar si partida está completa
                                      const isPartidaCompleta = row.partida_completa === true;
                                      //const isFieldEditable = col.editable && !isDetailFieldReadOnly(col, row);
                                      
                                      // 🔥 NUEVO: Si partida completa → Mostrar como texto plano
                                      if ((isPartidaCompleta || isReadOnly) && (!col.componentOnly || (col.viewOnly && !surtirMode))) {
                                        const value = row[col.name];

                                        if (col.type === 'number') {
                                          if (col.autoIncrement) {
                                              return (
                                                  <span 
                                                      className="text-xs text-gray-600"
                                                      style={{ 
                                                          display: 'block',
                                                          textAlign: col.align || 'center',
                                                          padding: '0.25rem 0.5rem'
                                                      }}
                                                  >
                                                      {row.numero}
                                                  </span>
                                              );
                                          }
                                          let decimals = col.displayDecimals;
                                          if (decimals === 'dynamic' && col.inputMask?.dynamicField) {
                                              decimals = parseInt(row[col.inputMask.dynamicField]) || 0;
                                          }
                                          const formattedValue = formatNumber(value, decimals);
                                          const showPriceAlert = col.priceAlert && row._precio_alerta;
                                          // const showPriceDrop = col.priceAlert && row._precio_bajo; // 🔒 futuro
                                          return (
                                            <span 
                                              className="text-xs text-gray-700"
                                              style={{ 
                                                  textAlign: col.align || 'right',
                                                  display: 'block',
                                                  padding: '0.25rem 0.5rem',
                                                  fontWeight: col.calculated ? '600' : 'normal'
                                              }}
                                            >
                                              {showPriceAlert && (
                                                <span 
                                                  className="relative" 
                                                  onMouseEnter={(e) => {
                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                    setPriceTooltipPosition({ top: rect.bottom + 8, left: rect.left });
                                                    setPriceTooltipRow(rowIndex);
                                                  }}
                                                  onMouseLeave={() => {
                                                    priceTooltipTimer.current = setTimeout(() => setPriceTooltipRow(null), 300);
                                                  }}
                                                >
                                                  ⚠️
                                                </span>
                                              )}
                                              {col.format === 'money' && value ? `$${formattedValue}` : formattedValue}
                                            </span>
                                          );
                                        }
                                        
                                        // aqui se cabio e bloque para edicion de partidas
                                        if (col.type === 'text') {
                                          const isDescLibre = col.name === 'producto' && row.deslibre === true;
                                          return (
                                            <span 
                                              className="text-xs text-gray-700"
                                              style={{ 
                                                textAlign: col.align || 'left',
                                                display: 'block',
                                                alignItems: 'center',
                                                gap: '0.25rem',
                                                padding: '0.25rem 0.5rem',
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                maxWidth: col.width || '450px'
                                              }}
                                            >
                                              {/* 🔥 Texto descripción */}
                                              <span style={{ flex: 1 }}>{value ?? ''}</span>

                                              {/* 🔥 Ícono lápiz solo si deslibre=true y no readonly */}
                                              {isDescLibre && !isReadOnly && (
                                                <span
                                                  //title={col.tooltip || '✏️ Click para editar descripción'} 
                                                  style={{ cursor: 'pointer', color: '#3b82f6', flexShrink: 0 }}
                                                  onMouseEnter={(e) => {
                                                    if (col.tooltip) showTooltip(col.tooltip, e.target);
                                                  }}
                                                  onMouseLeave={() => showTooltip(null, null)}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    showTooltip(null, null);
                                                    const newData = [...detailData];
                                                    newData[rowIndex]._editandoDescLibre = true;
                                                    setDetailData(newData);
                                                  }}
                                                >
                                                  ✏️
                                                </span>
                                              )}
                                            </span>
                                          );
                                        }

                                        // Fallback: unidad, porcentajes y cualquier otro tipo
                                        return (
                                          <span 
                                            className="text-xs text-gray-700"
                                            style={{ 
                                                display: 'block',
                                                padding: '0.25rem 0.5rem'
                                            }}
                                          >
                                            {value ?? ''}
                                          </span>
                                        );
                                      }

                                      if (col.autoIncrement) {
                                        return (
                                          <span className="text-gray-600 text-xs">
                                            {row.numero}
                                          </span>
                                        );
                                      }                                    
                                      if (col.calculated) {
                                        const value = row[col.name] || 0;
                                        return (
                                          <span className="font-semibold text-xs">
                                            {col.format === 'money' 
                                              ? `$${Number(value).toLocaleString('en-US', {
                                                  minimumFractionDigits: col.decimals || 2,
                                                  maximumFractionDigits: col.decimals || 2,
                                                })}`
                                              : value
                                            }
                                          </span>
                                        );
                                      }                                    
                                      if (!col.editable) {
                                        return (
                                          <span className="text-xs text-gray-700">
                                            {row[col.name] || ''}
                                          </span>
                                        );
                                      }
                                      if (col.type === 'number') {
                                        const isBlocked = invalidCodigoRow === rowIndex;
                                        let stepValue = "any";
                                        
                                        if (col.name === 'cantidad' && col.inputMask?.enabled && col.inputMask?.dynamicField) {
                                          const decimalesUnidad = parseInt(row[col.inputMask.dynamicField]) || 0;

                                          if (decimalesUnidad === 0) {
                                            stepValue = "1";
                                          } else if (decimalesUnidad === 2) {
                                            stepValue = "0.01";
                                          } else if (decimalesUnidad === 3) {
                                            stepValue = "0.001";
                                          }
                                        }
                                        
                                        return (
                                          <input
                                            type='text' //{focusedCell === `${rowIndex}-${col.name}` ? 'number' : 'text'}
                                            step={stepValue}
                                            value={(() => {
                                              if (focusedCell === `${rowIndex}-${col.name}`) {
                                                if (col.inputMask?.enabled && col.inputMask?.dynamicField) {
                                                  const dec = parseInt(row[col.inputMask?.dynamicField] ?? 0) || 0;
                                                  if (dec === 0) return parseInt(row[col.name]) || '';
                                                }
                                                return row[col.name] ?? '';
                                              }
                                              const rawValue = row[col.name];
                                              if (!rawValue && rawValue !== 0) return '';
                                              let decimals = col.displayDecimals;
                                              if (decimals === 'dynamic' && col.inputMask?.dynamicField) {
                                                decimals = parseInt(row[col.inputMask.dynamicField]) || 0;
                                              }                                            
                                              return formatNumber(rawValue, decimals);
                                            })()}
                                            data-row={rowIndex}
                                            data-field={col.name}
                                            onChange={(e) => {
                                              const valor = e.target.value;
                                              if (valor === '' || valor === null || valor === undefined) {
                                                updateDetailCellValue(rowIndex, col.name, '');
                                                return;
                                              }
                                              if (col.validation?.positive && valor !== '' && !isNaN(parseFloat(valor))) {
                                                const numValue = parseFloat(valor);
                                                if (numValue < 0) {
                                                  toast.error(`💀 ${col.label} negativo NO permitido. ¡Partida eliminada!`);
                                                  handleDeleteDetailRow(rowIndex);               
                                                  return;
                                                }
                                              }
                                              updateDetailCellValue(rowIndex, col.name, valor);
                                            }}
                                            onInput={(e) => {
                                              // Depuracion compartida con SqlexecBuilder (utils/numberFormatters).
                                              // Recorte por unidad solo con 0, 2 o 3 decimales, como antes.
                                              let decimalesUnidad = null;
                                              if (col.inputMask?.enabled && col.inputMask?.dynamicField) {
                                                const dec = parseInt(row[col.inputMask.dynamicField]) || 0;
                                                if (dec === 0 || dec === 2 || dec === 3) decimalesUnidad = dec;
                                              }
                                              e.target.value = sanitizeNumericInput(e.target.value, decimalesUnidad);
                                            }}

                                            onKeyDown={(e) => {
                                              if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setFocusedCell(null);
                                                showTooltip(null, null);
                                                const esNueva = detailData[rowIndex]?._id?.startsWith('new_');
                                                if (!col.componentOnly && (!isUpdate || esNueva)) handleDeleteDetailRow(rowIndex);
                                                return;
                                              }
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                
                                                // 🔥 NUEVO: Validar el valor ANTES de hacer blur
                                                const valor = e.target.value?.trim() || '';
                                                const numValue = parseFloat(valor);

                                                // ini-proceso por surtido pedido-remision 23/04/2025
                                                if (col.componentOnly) return handleComponentOnlyBlur(rowIndex, col, numValue, true);                                                
                                                // end-proceso por surtido pedido-remision 23/04/2025

                                                // Si es vacío o inválido → ELIMINAR directamente
                                                if (valor === '' || isNaN(numValue)) {
                                                  showTooltip(null, null);
                                                  setFocusedCell(null);
                                                  handleDeleteDetailRow(rowIndex);
                                                  return;
                                                }

                                                // Solo eliminar si campo requiere positivo Y valor <= 0
                                                if (col.validation?.positive && numValue <= 0) {
                                                  showTooltip(null, null);
                                                  setFocusedCell(null);
                                                  handleDeleteDetailRow(rowIndex);
                                                  return;
                                                }
                                                
                                                // 🔥 NUEVO: Actualizar el valor si es válido
                                                const newData = [...detailData];
                                                newData[rowIndex][col.name] = numValue;
                                                setDetailData(newData);

                                                // 🔥 AGREGAR AQUÍ → Tope máximo descuento
                                                if (col.name === 'pordesc') {
                                                  const topeMax = parseFloat(formData?.descuento_maximo_permitido) || 0;
                                                  if (topeMax > 0 && numValue > topeMax) {
                                                    toast.warning(`⚠️ Descuento máximo permitido: ${topeMax}%`);
                                                    newData[rowIndex][col.name] = topeMax;
                                                    setDetailData([...newData]);
                                                    return;
                                                  }
                                                }
                                                
                                                // Limpiar tooltip antes de navegar
                                                showTooltip(null, null);
                                                setFocusedCell(null);
                                                
                                                const navConfig = detailSchema?.navigation?.[col.name];
                                                
                                                if (navConfig?.onEnter) {
                                                  const nextField = navConfig.onEnter;
                                                  const nextCol = detailSchema?.columns?.find(c => c.name === nextField);
                                                  const nextIsBlocked = nextCol && isDetailFieldReadOnly(nextCol, row);
                                                  const efectiveNav = (nextIsBlocked && nextField !== 'completeRow')
                                                      ? 'completeRow'
                                                      : nextField;

                                                  if (efectiveNav !== 'completeRow') {
                                                      const nextInput = document.querySelector(
                                                          `input[data-row="${rowIndex}"][data-field="${efectiveNav}"]`
                                                      );
                                                      if (nextInput && !nextInput.disabled) {
                                                          nextInput.focus();
                                                          nextInput.select();
                                                      }
                                                  }

                                                  if (efectiveNav === 'completeRow') {
                                                    e.target.blur();

                                                    const esReedicion = isReeditingDetailRowRef.current;
                                                    isReeditingDetailRowRef.current = false;

                                                    setTimeout(() => {
                                                      const newData = [...detailData];
                                                      newData[rowIndex].partida_completa = true;
                                                      setDetailData(newData);

                                                      if (esReedicion) return;

                                                      setTimeout(() => {
                                                        const buttons = document.querySelectorAll('button[type="button"]');
                                                        const addButton = Array.from(buttons).find(btn =>
                                                          btn.textContent.includes('+ Agregar Partida')
                                                        );
                                                        if (addButton) {
                                                          addButton.focus();
                                                        }
                                                        setTimeout(() => scrollToTotals(), 100);
                                                      }, 50);
                                                    }, 50);
                                                  }
                                                }
                                              }
                                            }}

                                            onFocus={(e) => {
                                              setFocusedCell(`${rowIndex}-${col.name}`);
                                              requestAnimationFrame(() => {
                                                e.target.select();
                                              });
                                              // 🔥 GotFocus: Mostrar tooltip estilo VFP
                                              if (col.tooltip && !isDetailFieldReadOnly(col, row)) {
                                                showTooltip(col.tooltip, e.target);
                                              }
                                            }}

                                            onBlur={(e) => {
                                              setFocusedCell(null);
                                              showTooltip(null, null);
                                              
                                              const valor = e.target.value?.trim() || '';
                                              const numValue = parseFloat(valor);

                                              // ini-proceso por surtido de pedido-remision 23/04/2025
                                              if (col.componentOnly) return handleComponentOnlyBlur(rowIndex, col, numValue);
                                              // end-proceso por surtido de pedido-remision 23/04/2025
                                              
                                              // Si es 0, vacío o inválido → ELIMINAR
                                              if (valor === '' || valor === '0' || isNaN(numValue) || numValue <= 0) {
                                                handleDeleteDetailRow(rowIndex);
                                                return;
                                              }
                                              
                                              // 🔥 Valor válido → ACTUALIZAR
                                              const newData = [...detailData];
                                              newData[rowIndex][col.name] = numValue;
                                              setDetailData(newData);

                                              // 🔥 AGREGAR AQUÍ → Tope máximo descuento
                                              if (col.name === 'pordesc') {
                                                const topeMax = parseFloat(formData?.descuento_maximo_permitido) || 0;
                                                if (topeMax > 0 && numValue > topeMax) {
                                                  toast.warning(`⚠️ Descuento máximo permitido: ${topeMax}%`);
                                                  newData[rowIndex][col.name] = topeMax;
                                                  setDetailData([...newData]);
                                                  return;
                                                }
                                              }
                                              
                                              validateDetailField(col, numValue, rowIndex);
                                              
                                              // 🔥 NUEVO: Detectar si este campo completa la fila
                                              const navConfig = detailSchema?.navigation?.[col.name];
                                              
                                              if (navConfig?.onEnter === 'completeRow') {
                                                // 🔥 Si el campo tiene completeRow, marcar la partida
                                                const esReedicion = isReeditingDetailRowRef.current;
                                                isReeditingDetailRowRef.current = false;

                                                setTimeout(() => {
                                                  const updatedData = [...detailData];
                                                  updatedData[rowIndex].partida_completa = true;
                                                  setDetailData(updatedData);

                                                  if (esReedicion) return;

                                                  // 🔥 Opcional: Focus al botón "Agregar"
                                                  setTimeout(() => {
                                                    const buttons = document.querySelectorAll('button[type="button"]');
                                                    const addButton = Array.from(buttons).find(btn =>
                                                      btn.textContent.includes('+ Agregar Partida')
                                                    );
                                                    if (addButton) {
                                                      addButton.focus();
                                                    }
                                                    setTimeout(() => scrollToTotals(), 100);
                                                  }, 50);
                                                }, 50);
                                              }
                                            }}

                                            disabled={(isReadOnly && !col.componentOnly) || isBlocked || isDetailFieldReadOnly(col, row)}
                                            className={`w-full px-2 py-1 text-xs border rounded focus:outline-none ${
                                              isBlocked || isDetailFieldReadOnly(col, row)
                                                ? 'bg-gray-100 border-gray-200 cursor-not-allowed'
                                                : 'border-gray-300 focus:border-blue-500'
                                            }`}
                                            style={{ 
                                              textAlign: focusedCell === `${rowIndex}-${col.name}` ? 'left' : 'right',
                                              borderColor: focusedCell === `${rowIndex}-${col.name}` ? '#3b82f6' : 'transparent',
                                              backgroundColor: focusedCell === `${rowIndex}-${col.name}` ? '#ffffff' : 'transparent'
                                            }}
                                          />
                                        );
                                      }

                                      if (col.type === 'text') {
                                        return (
                                          <input
                                            type="text"
                                            value={row[col.name] || ''}
                                            data-row={rowIndex}
                                            data-field={col.name}

                                            onChange={(e) => {           
                                              const newData = [...detailData];
                                              let valor = e.target.value;
                                              if (col.pattern) {
                                                valor = valor.replace(/[^0-9]/g, '');
                                              }
                                              newData[rowIndex][col.name] = valor;
                                              setDetailData(newData);
                                            }}

                                            onFocus={(e) => {
                                              if (col.tooltip && !isDetailFieldReadOnly(col, row)) {
                                                showTooltip(col.tooltip, e.target);
                                              }
                                              e.target.select();
                                            }}
                                            
                                            onBlur={async (e) => {
                                              showTooltip(null, null);
                                              const fieldValue = e.target.value?.trim() || '';
                                              if (!col.searchable) return;
                                              if (!fieldValue) return;

                                              if (col.name === 'codigo') {
                                                const codigoNumerico = parseInt(fieldValue, 10);   
                                                if (!isNaN(codigoNumerico) && codigoNumerico <= 0) {
                                                  const newData = [...detailData];
                                                  newData[rowIndex].codigo = '';
                                                  setDetailData(newData);
                                                  e.target.value = '';
                                                  toast.warning('Código debe ser mayor a cero');
                                                  setTimeout(() => {
                                                    const codigoInput = document.querySelector(
                                                      `input[data-row="${rowIndex}"][data-field="codigo"]`
                                                    );
                                                    if (codigoInput) {
                                                      codigoInput.focus();
                                                      codigoInput.select();
                                                    }
                                                  }, 100);
                                                  return;
                                                }                                              
                                                // Mismo producto ya cargado en la fila → no buscar de nuevo (evita toast doble)
                                                const productIdField = detailSchema?.productIdField || 'idinvearts';
                                                if (row[productIdField] && String(row[productIdField]) === fieldValue) return;
                                                await handleDetailCellChange(rowIndex, col.name, fieldValue);                                              
                                              } else {
                                                handleDetailCellChange(rowIndex, col.name, fieldValue);
                                              }
                                            }}
                                            
                                            onKeyDown={(e) => {
                                              if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setFocusedCell(null);
                                                showTooltip(null, null);
                                                handleDeleteDetailRow(rowIndex);
                                                return;
                                              }
                                              
                                              if (e.shiftKey && (e.key === 'B' || e.key === 'b')) {
                                                if (col.searchModal?.enabled) {
                                                  const fieldValue = e.target.value || '';
                                                  const emptyRequired = col.searchModal.emptyRequired !== false;
                                                  
                                                  if (!emptyRequired || fieldValue.trim() === '') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    // 🔥 NUEVO: Limpiar el input actual para evitar caracteres residuales
                                                    if (e.target) {
                                                      e.target.blur(); // Quitar focus temporal
                                                    }
                                                    setSearchModalOriginInput(e.target);
                                                    setSearchModalState({
                                                      isOpen: true,
                                                      rowIndex: rowIndex,
                                                      fieldName: col.name,
                                                      config: col.searchModal
                                                    });
                                                  } else {
                                                    toast.warning(col.searchModal.emptyMessage || 'Campo debe estar vacío');
                                                  }
                                                  return;
                                                }
                                              }

                                              // 🔥 Tab/Enter → Validar campo requerido (zero hardcore)
                                              if (e.key === 'Enter' || e.key === 'Tab') {
                                                const fieldValue = e.target.value?.trim() || '';
                                                
                                                // Validar desde schema
                                                if (col.required && !fieldValue) {
                                                  e.preventDefault();
                                                  toast.warning(`${col.label} es requerido`);
                                                  
                                                  // Mantener focus
                                                  setTimeout(() => {
                                                    e.target.focus();
                                                    e.target.select();
                                                  }, 10);
                                                  return;
                                                }
                                                
                                                // Si tiene valor → Continuar
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  e.target.blur();
                                                }
                                              }                                            
                                            }}                                          
                                            disabled={isReadOnly || isDetailFieldReadOnly(col, row)}                                          
                                            className={`w-full px-2 py-1 text-xs border rounded focus:outline-none ${
                                              isDetailFieldReadOnly(col, row)
                                                ? 'bg-gray-100 border-gray-200 cursor-not-allowed' 
                                                : 'border-gray-300 focus:border-blue-500'
                                            }`}                                          
                                            style={{ textAlign: col.align || 'left' }}
                                          />
                                        );
                                      }

                                      //Columna tipo checkbox seleccion de partidas completas o individuales
                                      if (col.type === 'checkbox' && col.fillOnCheck) {
                                        return (
                                          <input
                                            type="checkbox"
                                            checked={!!row[col.name]}
                                            onChange={(e) => {
                                              const val = e.target.checked ? parseFloat(row[col.fillOnCheck.source]) || 0 : 0;
                                              updateDetailCellValue(rowIndex, col.fillOnCheck.target, val);
                                              const newData = [...detailData];
                                              newData[rowIndex][col.name] = e.target.checked;
                                              setDetailData([...newData]);
                                            }}
                                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                          />
                                        );
                                      }

                                      return null;
                                    })()}
                                  </td>
                                ))}

                                {/* Celda Acciones */}
                                {!isReadOnly && (
                                  <td 
                                    className="whitespace-nowrap"
                                    style={{
                                      paddingLeft: tabla_ui?.actionsColumn?.cell?.padding?.x || '0.75rem',
                                      paddingRight: tabla_ui?.actionsColumn?.cell?.padding?.x || '0.75rem',
                                      paddingTop: tabla_ui?.actionsColumn?.cell?.padding?.y || '0.25rem',
                                      paddingBottom: tabla_ui?.actionsColumn?.cell?.padding?.y || '0.25rem',
                                      fontSize: rowsConfig.fontSize || '0.75rem',
                                      borderRight: rowsConfig.borderRight || '1px solid #e5e7eb',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}
                                  >
                                    <div 
                                      className="flex items-center"
                                      style={{
                                        justifyContent: tabla_ui?.actionsColumn?.cell?.align === 'left' 
                                          ? 'flex-start' 
                                          : tabla_ui?.actionsColumn?.cell?.align === 'center'
                                          ? 'center'
                                          : 'flex-end'
                                      }}
                                    >
                                      <DropdownMenu
                                        item={row}
                                        accionesDisponibles={getDetailRowAcciones(row)}
                                        onAccionClick={handleDetailAccionClick}
                                        disabled={!isDetailRowMenuEnabled(row, rowIndex)}
                                        tabla_ui={tabla_ui}
                                      />
                                    </div>
                                  </td>
                                )}
                              </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>                    
                    
                    {totalsSchema && (
                      <div className="mt-4" ref={totalsRef}>
                        {totalsSchema.showLabel !== false && totalsSchema.label && (
                          <label
                            className="block"
                            style={{
                              fontSize: totalsSchema.labelStyles?.fontSize || '0.875rem',
                              fontWeight: totalsSchema.labelStyles?.fontWeight || '600',
                              color: totalsSchema.labelStyles?.color || '#374151',
                              marginBottom: totalsSchema.labelStyles?.marginBottom || '0.5rem'
                            }}
                          >
                            {totalsSchema.label}
                          </label>
                        )}
                        <div 
                          className="overflow-hidden"
                          style={{
                            border: tabla_ui?.table?.border || '1px solid #d1d5db'
                          }}
                        >
                          <table 
                            className="w-full text-sm"
                            style={{
                              border: tabla_ui?.table?.border || '1px solid #d1d5db',
                              borderCollapse: tabla_ui?.table?.borderCollapse || 'collapse'
                            }}
                          >
                            <thead>
                              <tr style={{
                                backgroundColor: headerConfig.backgroundColor || '#f3f4f6'
                              }}>
                                {totalsSchema.columns?.map(col => (
                                  <th
                                    key={col.field}
                                    className="whitespace-nowrap"
                                    style={{
                                     width: col.width,
                                      textAlign: 'center',
                                      paddingLeft: headerConfig.padding?.x || '0.50rem',
                                      paddingRight: headerConfig.padding?.x || '0.50rem',
                                      paddingTop: headerConfig.padding?.y || '0.50rem',
                                      paddingBottom: headerConfig.padding?.y || '0.50rem',
                                      fontSize: headerConfig.fontSize || '0.75rem',
                                      fontFamily: headerConfig.fontFamily || 'inherit',
                                      fontWeight: headerConfig.fontWeight || '600',
                                      textTransform: headerConfig.textTransform || 'uppercase',
                                      color: headerConfig.color || '#374151',
                                      borderRight: headerConfig.borderRight || '1px solid #d1d5db',
                                      borderTop: headerConfig.borderTop || '1px solid #d1d5db',
                                      borderBottom: headerConfig.borderBottom 
                                        ? `${headerConfig.borderBottom.width} ${headerConfig.borderBottom.style} ${headerConfig.borderBottom.color}`
                                        : '2px solid #e5e7eb'
                                    }}
                                  >
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detailData.length === 0 ? (
                                <tr>
                                  <td
                                    colSpan={totalsSchema.columns?.length}
                                    style={{
                                      textAlign: 'center',
                                      padding: '0.75rem',
                                      fontSize: rowsConfig.fontSize || '0.75rem',
                                      fontFamily: rowsConfig.fontFamily || 'inherit',
                                      color: '#6b7280',
                                      height: rowsConfig.height || '32px',
                                      borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                    }}
                                  >
                                    💰 Sin partidas • Los totales se calcularán automáticamente
                                  </td>
                                </tr>
                              ) : (
                                <tr
                                  className="hover:bg-gray-50"
                                  style={{
                                    backgroundColor: rowsConfig.odd || '#ffffff',
                                    height: rowsConfig.height || '32px'
                                  }}
                                >
                                  {totalsSchema.columns?.map(col => (
                                    <td
                                      key={col.field}
                                      style={{
                                        textAlign: 'center',
                                        paddingLeft: '0.5rem',
                                        paddingRight: '0.5rem',
                                        paddingTop: '0.125rem',
                                        paddingBottom: '0.125rem',
                                        fontSize: rowsConfig.fontSize || '0.75rem',
                                        fontFamily: rowsConfig.fontFamily || 'inherit',
                                        color: rowsConfig.textColor || '#374151',
                                        fontWeight: col.field === 'total' ? '600' : 'normal',
                                        borderRight: rowsConfig.borderRight || '1px solid #e5e7eb',
                                        borderBottom: rowsConfig.borderBottom || '1px solid #e5e7eb'
                                      }}
                                    >
                                      {formatTotalNumber(totals[col.field] || 0, col.format, col.decimals || 2)}
                                    </td>
                                  ))}
                                </tr>
                              )}
                            </tbody>                                                       
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </form>

              {componentForm === 'Sys_Envio_Email' && (saldoCorreos === -1 || saldoCorreos === 0) && (
                <div style={{
                  paddingLeft: modales_ui?.footer?.padding?.x || '2rem',
                  paddingRight: modales_ui?.footer?.padding?.x || '2rem',
                  paddingTop: '0.5rem',
                  textAlign: 'right'
                }}>
                  <span style={{ fontSize: formulario_ui?.errorMessages?.fontSize || '0.75rem', color: formulario_ui?.errorMessages?.textColor || '#dc2626' }}>
                    {saldoCorreos === -1 ? 'Servicio de correos no configurado' : 'Sin saldo de correos disponibles'}
                  </span>
                </div>
              )}

              {(() => {
                const btnBase = botones_ui || {};
                return (
                  <div
                    className="flex justify-between items-center"
                    style={{
                      paddingLeft: modales_ui?.footer?.padding?.x || '2rem',
                      paddingRight: modales_ui?.footer?.padding?.x || '2rem',
                      paddingTop: modales_ui?.footer?.padding?.y || '1.5rem',
                      paddingBottom: modales_ui?.footer?.padding?.y || '1.5rem',
                      borderTop: modales_ui?.footer?.borderTop || '1px solid #e5e7eb',
                      backgroundColor: modales_ui?.footer?.backgroundColor || 'transparent'
                    }}
                  >
                    {/* 🔥 IZQUIERDA: Botón Agregar Partida */}
                    <div>
                      {detailSchema?.allowAdd && !isReadOnly ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            // 🔥 Validar master fields desde JSON
                            const faltante = getMasterFieldMensaje();
                            if (faltante) {
                              showTooltip(faltante.message, e.target);
                              setTimeout(() => showTooltip(null, null), faltante.tooltipTimeout || 3000);
                              return;
                            }
                            handleAddDetailRow();
                          }}
                          disabled={hayFilasIncompletas() || hayMasterFieldsFaltantes()}
                          className="transition-colors"
                          style={{
                            height: btnBase.height || '40px',
                            fontSize: btnBase.fontSize || '0.875rem',
                            fontFamily: btnBase.fontFamily || 'inherit',
                            fontWeight: btnBase.fontWeight || '500',
                            paddingLeft: btnBase.padding?.x || '1rem',
                            paddingRight: btnBase.padding?.x || '1rem',
                            paddingTop: btnBase.padding?.y || '0.5rem',
                            paddingBottom: btnBase.padding?.y || '0.5rem',
                            borderRadius: btnBase.borderRadius || '6px',
                            borderWidth: btnBase.borderWidth || '1px',
                            borderColor: (hayFilasIncompletas() || hayMasterFieldsFaltantes())
                              ? '#9ca3af'
                              : (botones_ui?.primary?.borderColor || '#3b82f6'),
                            backgroundColor: (hayFilasIncompletas() || hayMasterFieldsFaltantes())
                              ? '#9ca3af'
                              : (botones_ui?.primary?.backgroundColor || '#3b82f6'),
                            color: botones_ui?.primary?.textColor || '#ffffff',
                            cursor: (hayFilasIncompletas() || hayMasterFieldsFaltantes()) ? 'not-allowed' : 'pointer',
                            opacity: (hayFilasIncompletas() || hayMasterFieldsFaltantes()) ? '0.5' : '1'
                          }}
                          onMouseEnter={(e) => {
                            if (!hayFilasIncompletas() && !hayMasterFieldsFaltantes()) {
                              e.target.style.backgroundColor = botones_ui?.primary?.hoverBackgroundColor || '#2563eb';
                              e.target.style.borderColor = botones_ui?.primary?.hoverBorderColor || '#2563eb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!hayFilasIncompletas() && !hayMasterFieldsFaltantes()) {
                              e.target.style.backgroundColor = botones_ui?.primary?.backgroundColor || '#3b82f6';
                              e.target.style.borderColor = botones_ui?.primary?.borderColor || '#3b82f6';
                            }
                          }}
                        >
                          + Agregar Partida
                        </button>
                      ) : (
                        <div></div>
                      )}
                    </div>

                    {/* 🔥 DERECHA: Botones Cancelar y Guardar */}
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="transition-colors"
                        style={{
                          height: btnBase.height || '40px',
                          fontSize: btnBase.fontSize || '0.875rem',
                          fontFamily: btnBase.fontFamily || 'inherit',
                          fontWeight: btnBase.fontWeight || '500',
                          paddingLeft: btnBase.padding?.x || '1rem',
                          paddingRight: btnBase.padding?.x || '1rem',
                          paddingTop: btnBase.padding?.y || '0.5rem',
                          paddingBottom: btnBase.padding?.y || '0.5rem',
                          borderRadius: btnBase.borderRadius || '6px',
                          borderWidth: btnBase.borderWidth || '1px',
                          borderColor: botones_ui?.secondary?.borderColor || '#d1d5db',
                          backgroundColor: botones_ui?.secondary?.backgroundColor || '#ffffff',
                          color: botones_ui?.secondary?.textColor || '#374151',
                          cursor: saving ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = botones_ui?.secondary?.hoverBackgroundColor || '#f9fafb';
                            e.target.style.borderColor = botones_ui?.secondary?.hoverBorderColor || '#9ca3af';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving) {
                            e.target.style.backgroundColor = botones_ui?.secondary?.backgroundColor || '#ffffff';
                            e.target.style.borderColor = botones_ui?.secondary?.borderColor || '#d1d5db';
                          }
                        }}
                      >
                        Cancelar
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (useGenerico) {
                            if (schema?.submitAction === 'confirm_only') {
                              executeSubmit();
                            } else if (schema?.submitAction === 'return_data') {
                              setShowConfirmaMensaje(true);
                            } else {
                              setShowConfirmaEmail(true);
                            }
                          } else {
                            setShowConfirmaMensaje(true);
                          }
                        }}
                        disabled={saving || !isFormValid || (isComponentMode && totals.total <= 0) || (componentForm === 'Sys_Envio_Email' && (saldoCorreos === -1 || saldoCorreos === 0))}
                        className="transition-colors"
                        style={{
                          height: btnBase.height || '40px',
                          fontSize: btnBase.fontSize || '0.875rem',
                          fontFamily: btnBase.fontFamily || 'inherit',
                          fontWeight: btnBase.fontWeight || '500',
                          paddingLeft: btnBase.padding?.x || '1rem',
                          paddingRight: btnBase.padding?.x || '1rem',
                          paddingTop: btnBase.padding?.y || '0.5rem',
                          paddingBottom: btnBase.padding?.y || '0.5rem',
                          borderRadius: btnBase.borderRadius || '6px',
                          borderWidth: btnBase.borderWidth || '1px',
                          borderColor: (saving || !isFormValid)
                            ? '#9ca3af'
                            : (confirmHeader?.btn_color || botones_ui?.success?.borderColor || '#16a34a'),
                          backgroundColor: (saving || !isFormValid)
                            ? '#9ca3af'
                            : (confirmHeader?.btn_color || botones_ui?.success?.backgroundColor || '#16a34a'),
                          color: botones_ui?.primary?.textColor || '#ffffff',
                          cursor: (saving || !isFormValid) ? 'not-allowed' : 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          if (!saving && isFormValid) {
                            e.target.style.backgroundColor = confirmHeader?.btn_hover || botones_ui?.success?.hoverBackgroundColor || '#15803d';
                            e.target.style.borderColor = confirmHeader?.btn_hover || botones_ui?.success?.hoverBorderColor || '#15803d';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!saving && isFormValid) {
                            e.target.style.backgroundColor = confirmHeader?.btn_color || botones_ui?.success?.backgroundColor || '#16a34a';
                            e.target.style.borderColor = confirmHeader?.btn_color || botones_ui?.success?.borderColor || '#16a34a';
                          }
                        }}
                      >
                        {saving ? (
                          <>
                            <span className="inline-block animate-spin mr-2">⏳</span>
                            Aplicando...
                          </>
                        ) : (
                          confirmBtnLabel || (mode === 'create' ? 'Crear' : 'Guardar')
                        )}
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}          
        </div>

        {/* 🔥 Tooltip dinámico estilo VFP */}
        {activeTooltip && !isDeleting && (  // 🎯 Agregar !isDeleting
          <div
            style={{
              position: 'absolute',
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
              backgroundColor: '#ccfbf1',
              border: 'none',
              padding: '6px 10px',
              fontSize: '11px',
              fontFamily: 'Aptos, Arial, sans-serif',
              color: '#374151',
              whiteSpace: 'nowrap',
              zIndex: 10000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              borderRadius: '4px',
              pointerEvents: 'none',
            }}
          >
            {activeTooltip}
          </div>
        )}

        {priceTooltipRow !== null && (
          <div
            className="fixed z-[9999] bg-white border border-amber-300 rounded-lg shadow-lg p-3 text-left"
            style={{ top: `${priceTooltipPosition.top}px`, left: `${priceTooltipPosition.left}px`, minWidth: '200px' }}
            onMouseEnter={() => clearTimeout(priceTooltipTimer.current)}
            onMouseLeave={() => {
              priceTooltipTimer.current = setTimeout(() => setPriceTooltipRow(null), 300);
            }}
          >
            <div className="text-amber-600 font-semibold text-xs mb-1">⚠️ Precio incrementó</div>
            <div className="text-xs text-gray-600">Cotizado: <span className="font-medium">${detailData[priceTooltipRow]?.precioreal}</span></div>
            <div className="text-xs text-gray-600">Actual: <span className="font-medium">${detailData[priceTooltipRow]?.precio_actual_catalogo}</span></div>
            <div className="text-xs text-gray-600">Diferencia: <span className="font-medium text-red-500">+${(detailData[priceTooltipRow]?.precio_actual_catalogo - detailData[priceTooltipRow]?.precioreal).toFixed(2)}</span></div>e
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const rowIndex = priceTooltipRow;
                const row = detailData[rowIndex];
                const newData = [...detailData];
                const tcVal = parseFloat(initialData.tipocambio) || 1;
                const precioActual = (row.esbase === false && formData.cotizacion_esbase === true && tcVal > 1)
                  ? parseFloat((row.precio_actual_catalogo * tcVal).toFixed(4))
                  : (row.esbase === true && formData.cotizacion_esbase === false && tcVal > 1)
                  ? parseFloat((row.precio_actual_catalogo / tcVal).toFixed(4))
                  : parseFloat(row.precio_actual_catalogo);

                //console.log('🔍 DEBUG:', {
                //  esbase: row.esbase,
                //  cotizacion_esbase: formData.cotizacion_esbase,
                //  tcVal,
                //  precio_actual_catalogo: row.precio_actual_catalogo,
                //  precioActual
                //});                

                newData[rowIndex].precio = precioActual;
                newData[rowIndex].precioreal = parseFloat(row.precio_actual_catalogo);
                newData[rowIndex]._precio_alerta = false;
                const totalesPartida = calcularTotalesPartida(
                  newData[rowIndex],
                  formData.cotizacion_esbase ?? true,
                  formData.tipocamb_valor ?? 1,
                  null,
                  formData.c_regimenfiscal ?? '000'
                );
                newData[rowIndex].importe_bruto = totalesPartida.importe_bruto;
                newData[rowIndex].imptotal = totalesPartida.imptotal;
                newData[rowIndex].impiva = totalesPartida.impiva;
                newData[rowIndex].impdesc = totalesPartida.impdesc;
                newData[rowIndex].total = totalesPartida.imptotal;
                newData[rowIndex].impuestos_extra = totalesPartida.impuestos_extra;
                setDetailData(newData);
                setIsDirty(true);
                setPriceTooltipRow(null);
                toast.success(`✅ Precio actualizado a $${precioActual}`);
              }}
              className="mt-2 w-full text-xs bg-amber-500 hover:bg-amber-600 text-white rounded px-2 py-1 transition-colors"
            >
              🔄 Actualizar al precio actual
            </button>
          </div>
        )}
      </div>

      <ConflictModal />

      <SqlexecMensaje
        show={showMensaje}
        type={mensajeData.type}
        title={mensajeData.title}
        message={mensajeData.message}
        steps={mensajeData.steps}
        technicalInfo={mensajeData.technicalInfo}
        actions={[
          {
            label: 'Entendido',
            variant: 'primary',
            onClick: () => {
              setShowMensaje(false);
              if (mensajeData.onDismiss) mensajeData.onDismiss();
              else onClose?.();
            }
          }
        ]}
        onClose={() => {
          setShowMensaje(false);
          if (mensajeData.onDismiss) mensajeData.onDismiss();
          else onClose?.();
        }}
      ></SqlexecMensaje>

      {/* 🔥 SearchModalField modo "modal-only" - Shift+B */}
      {searchModalState.isOpen && searchModalState.config && (
        <SearchModalField
          field={{
            name: `temp_search_${searchModalState.fieldName}`,
            label: searchModalState.config.label || 'Búsqueda',
            searchConfig: {
              componentForm: searchModalState.config.componentForm,
              valueField: searchModalState.config.valueField,
              displayFields: searchModalState.config.displayFields,
              displayFormat: searchModalState.config.displayFormat,
              returnFields: searchModalState.config.returnFields
            }
          }}
          value={null}
          onChange={() => {}}
          onMultiFieldChange={(fields) => {
            const { rowIndex, fieldName } = searchModalState;
            const selectedId = fields[searchModalState.config.valueField];            
            searchModalSelectedRef.current = true;
            setSearchModalState({ isOpen: false, rowIndex: null, fieldName: null, config: null });
            setSearchModalOriginInput(null);            
            handleDetailCellChange(rowIndex, fieldName, selectedId);
          }}
          disabled={false}
          error={null}
          touched={false}
          inputStyles={{}}
          labelStyles={{}}
          errorStyles={{}}
          renderMode="modal-only"
          externalControl={true}
          isOpen={searchModalState.isOpen}
          onOpenChange={(newState) => {
            if (!newState) {
              setSearchModalState({ isOpen: false, rowIndex: null, fieldName: null, config: null });
              if (searchModalSelectedRef.current) {
                searchModalSelectedRef.current = false;
                setSearchModalOriginInput(null);
                return;
              }
              setTimeout(() => {
                if (searchModalOriginInput) {
                  searchModalOriginInput.focus();
                  searchModalOriginInput.select();
                  setSearchModalOriginInput(null); // Limpiar referencia
                }
              }, 100);
            }
          }}
        />
      )}
      {showConfirmaDelete && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo="danger"
          pregunta={`¿Deseas eliminar la partida ${rowToDelete !== null ? (detailData[rowToDelete]?.producto || 'seleccionada') : 'seleccionada'}?`}
          onClose={() => { setShowConfirmaDelete(false); setRowToDelete(null); }}
          onConfirm={() => { setShowConfirmaDelete(false); confirmDeleteDetailRow(); }}
        />
      )}
      {showConfirmaMensaje && schema?.submitAction !== 'return_data' && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo="warning"
          pregunta={surtirMode
            ? '¿Deseas generar la remisión con las cantidades surtidas?'
            : `¿Deseas guardar los cambios de ${schema?.title || 'este documento'}?`}
          onClose={() => setShowConfirmaMensaje(false)}
          onConfirm={() => { setShowConfirmaMensaje(false); executeSubmit(); }}
        />
      )}

      {showConfirmaMensaje && schema?.submitAction === 'return_data' && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo="danger"
          pregunta="¿Deseas continuar con la cancelación de la remisión seleccionada?"
          onClose={() => setShowConfirmaMensaje(false)}
          onConfirm={() => { setShowConfirmaMensaje(false); executeSubmit(); }}
        />
      )}
      {showConfirmaEmail && (
        <SqlexecConfirmDialog
          isOpen={true}
          tipo="warning"
          pregunta={`¿Deseas enviar ${formData.titulo || `${formData.folio_label || ''} #${formData.folio_id || ''}`} a ${formData.cc ? `${formData.email || ''} / C.c. ${formData.cc}` : (formData.email || '')}?`}
          onClose={() => setShowConfirmaEmail(false)}
          onConfirm={() => { setShowConfirmaEmail(false); executeSubmit(); }}
        />
      )}
    </>
  );
};

export default SqlexecMasterDetail;